import React, {
  forwardRef,
  useState,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useId,
} from 'react';
import styled from 'styled-components';
import { semantic, semanticColor, surface } from '@repo/design-tokens';
import {
  clipPolygonToRect,
  pointInPolygon,
  polygonArea,
  type HeatBlobOutline,
} from '@repo/front-end-utils';
import { Button, HoverCard, Icon } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';
import {
  EDIT_DIM,
  KEYBOARD_HANDLE_FILL,
  REGION_STYLE,
  ownRegionKind,
  type RegionKind,
} from './regionStyle';
import { HEAT_GRID_CELL_PX } from './heatGridImage';
import type { HeatGridImage } from './heatGridImage';
import { markHeatGrid } from './heatGridTiming';

export interface Point {
  x: number;
  y: number;
}

export interface Polygon {
  id: string;
  points: Point[];
  isComplete: boolean;
}

/**
 * Overlapping regions, smallest first (`observer-overlap-pick`, FR2): the order every hit-test walks,
 * and — reversed — the order regions are drawn in, so the smallest sits in front and is what a click
 * reaches. A tie goes to the later region, as last-drawn-wins did before. Area is the shoelace area,
 * so a self-intersecting (bow-tie) region, whose lobes cancel, ranks as small as it measures.
 */
const bySmallestArea = <T extends { points: Point[] }>(list: T[]): T[] =>
  list
    .map((item, index) => ({ item, index, area: polygonArea(item.points) }))
    .sort((a, b) => a.area - b.area || b.index - a.index)
    .map(({ item }) => item);

/**
 * Of the open entity's region and another entity's, both under the pointer, the one a click reaches:
 * the smaller (`observer-overlap-pick`, ruling 2026-10-04 — smallest wins, own or not). A tie keeps
 * the open entity's own region, which is drawn above the backdrop.
 */
const otherIsSmaller = (own: { points: Point[] }, other: { points: Point[] }): boolean =>
  polygonArea(other.points) < polygonArea(own.points);

/**
 * Tab+click's step through the regions under the pointer (`observer-unified-draw-edit`, R5.3): the
 * stack is smallest first, as a plain click ranks it. From the selected region at position i, the
 * next (i+1, or i-1 with `back`), wrapping. With the selected region not in the stack, the second
 * entry — the one a plain click cannot reach — or the only one. An empty stack gives nothing.
 */
export const stepInStack = <T extends { id: string }>(
  stack: readonly T[],
  selectedId: string | null,
  back = false,
): T | null => {
  if (stack.length === 0) return null;
  const at = selectedId === null ? -1 : stack.findIndex(entry => entry.id === selectedId);
  if (at < 0) return stack[stack.length > 1 ? 1 : 0];
  return stack[(at + (back ? -1 : 1) + stack.length) % stack.length];
};

/** One region in the stack under a point: the open entity's own, or another entity's. */
interface StackEntry {
  id: string;
  own: boolean;
  polygon: Polygon;
}

interface Props {
  className?: string;
  imgSrc: string | null;
  onPolygonChange?: (polygons: Polygon[]) => void;
  multiPolygon?: boolean;
  initialPolygonSet?: Polygon[]; // Load multiple complete polygons (replaces on change)
  framePolygons?: Polygon[]; // Existing polygons from other services in the same frame
  /*
   * A change of picture (`imgSrc`) keeps every region: each one is an observer the host files, and a
   * new picture of the same camera (*Refresh picture*) must not delete it. The host keys the plotter
   * by camera, so a different camera is a fresh mount.
   */
  /**
   * The heat HISTORY as one image (`observer-autogen-heat-history-view`, R4): a cell-resolution
   * picture (one pixel per 16 px cell) drawn ×16 in the picture's own pixels. The host owns the
   * read (so token/baseUrl plumbing stays in pages); `heatmapLoading` marks the canvas busy while it
   * reads. The per-frame dots it once sat beside are retired (`observers-heat-follows-tracked-part`,
   * R4). `label` is the image's accessible name — the caption's facts (Block A3).
   */
  heatImage?: (HeatGridImage & { label?: string }) | null;
  /** The picture's natural size, reported when it loads — the host sizes the heat image from it. */
  onNaturalSize?: (size: { w: number; h: number } | null) => void;
  heatmapLoading?: boolean;
  // The heat's visibility — the host's alone (default off): the fetch needs a stream BID, a token
  // and a base URL that only the host has, and the host defaults it ON
  // (`camera-observer-setup-workspace`, FR6).
  heatmapVisible?: boolean;
  /**
   * CONTROLLED visibility of `framePolygons` — the host's *Observers* layer switch
   * (`observers-page-camera-stage`). Omitted, they are shown.
   */
  framePolygonsVisible?: boolean;
  // What the empty canvas says instead of "Awaiting camera feed…". A host that KNOWS the
  // picture has failed passes the truth here, so the placeholder and the canvas's name stop
  // promising a wait for something that is not coming. Default: the awaiting sentence.
  emptyMessage?: string;
  // What to say about each region when the pointer rests on it: a name and a detail line, keyed by
  // polygon id — the plotter's own polygons by their id, the backdrop (`framePolygons`) by theirs.
  // A region with no entry shows no tip. Supplying it also describes every visible tipped region
  // to assistive technology, through the canvas's `aria-describedby`, because a hover tip is not
  // reachable without a pointer.
  polygonTips?: Record<string, PolygonTip>;
  // The region the HOST has selected — the observer strip — as one of the plotter's own polygon
  // ids. Its tip is shown while the canvas has keyboard focus and no region is hovered, so choosing
  // an observer from the strip and tabbing to the picture names it without a pointer. With
  // `onSelectRegion` it is the selection itself (controlled): the plotter keeps none of its own.
  selectedRegionId?: string | null;
  /*
   * ── From #1865 (`multi-observer-modal`), merged for the camera stage ──────────────────────────
   */
  /** The own regions are a region waiting for its entity — drawn dashed until it is linked. */
  regionsWaiting?: boolean;
  /**
   * Whether drawing is allowed here (`observer-unified-draw-edit`, R3.5): a Shift press, or any press
   * while `drawArmed`, starts a region only when it is. A plain click never starts one. Default `true`.
   */
  allowDraw?: boolean;
  /**
   * The host's *Add Observer* has armed one region (`observer-unified-draw-edit`, R4): the next
   * unmoved press starts it, as a Shift press would. The host disarms on `onDrawStart`.
   */
  drawArmed?: boolean;
  /** A region's first point was placed, by Shift or by `drawArmed`. */
  onDrawStart?: () => void;
  /**
   * A Tab+click selected the region at 1-based `position` of the `count` regions under the pointer
   * (`observer-unified-draw-edit`, R5): after the selection or pick it made, so the host can say it.
   */
  onStackStep?: (regionId: string, position: number, count: number) => void;
  /** Pick a BACKDROP region by clicking it; its outline lights on hover. */
  onPickRegion?: (frameRegionId: string) => void;
  /** The backdrop region a click would pick, as it changes — so the host can light its row. */
  onHoverRegion?: (frameRegionId: string | null) => void;
  /**
   * Delete, owned by the host: when given, the Delete key on a selected region calls this with its
   * id instead of removing it here, so it takes the host's path — Undo, the toast, the announcement.
   */
  onDeleteKey?: (polygonId: string) => void;
  /**
   * A region of the open entity's own the operator clicked — every click, the same region again
   * included — so the host's entity card, tracking card and *Delete Observer* act on it
   * (`observers-page-camera-stage`, FR8, FR10, FR15). Supplying it makes the selection controlled:
   * `selectedRegionId` is the truth, and Delete acts on it.
   */
  onSelectRegion?: (polygonId: string | null) => void;
  /** A plain click on empty picture — no region under it, no travel: deselect (FR20). */
  onClearSelection?: () => void;
  /** Another element that describes the canvas — the host's keyboard-shortcut list (FR12). */
  describedBy?: string;
  /** The picture's accessible name, from a host that knows more than the plotter. */
  canvasLabel?: string;
  /**
   * A reshape's start and end: one `start` and one `end` per corner drag, and per run of arrow-key
   * nudges of a selected corner, so the host makes each one undo step however long it lasts. A
   * gesture cut short (a reseed, an unmount) still reports its `end`.
   */
  onEditGesture?: (phase: 'start' | 'end') => void;
  /*
   * ── New for the camera stage (`observers-page-camera-stage`, FR4, FR5, FR11) ─────────────────
   */
  /** The open region's point count whenever it changes; 0 when no region is open. */
  onDrawingChange?: (points: number) => void;
  /** The zoom, in percent of the fitted picture (100 = fitted), whenever it changes. */
  onZoomChange?: (percent: number) => void;
  /*
   * ── Suggestions (`observer-autogen-review-ui-preview`, R4, R7) ──────────────────────────────────
   */
  /**
   * The engine's suggested observers, in the picture's own pixels: drawn read-only (no hit-test, no
   * selection, no drag) as dashed suggestion-edge outlines (`REGION_STYLE.suggestion`) with a one-line label, and listed in the canvas's
   * description. The host owns the read.
   */
  suggestions?: SuggestionOutline[];
  /** CONTROLLED visibility of `suggestions` — the host's *Suggestions* switch. Default off. */
  suggestionsVisible?: boolean;
  /**
   * Where the engine thinks each machine is (`observer-matching-beyond-geometry-machine-outline`):
   * its floor-plan outline through the camera's calibration, in the picture's own pixels. Drawn
   * read-only beneath the suggestions, only while `suggestionsVisible`, with no label, and named in
   * the canvas's description. An entry with fewer than 3 points is not drawn.
   */
  machineOutlines?: MachineOutline[];
  /**
   * Outlines round the blobs of heat (`observer-matching-beyond-geometry-heat-outlines`), in the
   * picture's own pixels, drawn over the heat while it shows. A click on one where no region is
   * under the pointer, or `Enter` on the one `O` reached, adds its shape as a region through
   * the same path that closes a hand-drawn one, so the host cannot tell them apart.
   */
  heatOutlines?: HeatBlobOutline[];
  /** Told after an outline is taken, so the host can say so. */
  onOutlineTaken?: () => void;
  /** Told when `O` or Shift+`O` reaches an outline (1-based, of how many), so the host can say which. */
  onOutlineHighlight?: (position: number, count: number) => void;
}

/** A suggested observer, as the drawing area needs it. */
export interface SuggestionOutline {
  id: string;
  points: Point[];
  label: string;
  /**
   * Drawn faint and unlabelled (`observer-autogen-review-walkthrough`, R3.2): the other machines'
   * suggestions behind the one under review, when the host asks for all of them.
   */
  faint?: boolean;
}

/** A machine's outline on the picture, as the drawing area needs it. */
export interface MachineOutline {
  id: string;
  /** The machine it belongs to: a machine seen through several views has one outline per view. */
  machine: string;
  points: Point[];
  /** The machine's name, for the canvas's description. */
  name: string;
  /** Drawn faint: every machine's outline behind the ones under review (*Show every machine outline*). */
  faint?: boolean;
}

/** How faint a background suggestion is drawn (R3.2). */
export const FAINT_SUGGESTION_OPACITY = 0.35;

const NO_SUGGESTIONS: SuggestionOutline[] = [];
const NO_MACHINE_OUTLINES: MachineOutline[] = [];
const NO_HEAT_OUTLINES: HeatBlobOutline[] = [];
const outlinePoints = (outline: HeatBlobOutline): Point[] =>
  outline.points.map(([x, y]) => ({ x, y }));

/**
 * How much bigger a taken heat outline is than the outline drawn
 * (`observer-matching-beyond-geometry-heat-outline-margin`, Mike 2026-11-02): 10 % in each direction,
 * so the observer keeps a margin round the heat that made it.
 */
export const TAKEN_OUTLINE_SCALE = 1.1;

/**
 * A taken outline's region: the same shape, scaled by `TAKEN_OUTLINE_SCALE` about its area centroid,
 * then clipped to the picture (`clipPolygonToRect`), so a diagonal edge near the picture's edge keeps
 * every bit of heat it covered. Without a picture size, unclipped.
 */
export const enlargedOutlinePoints = (
  outline: HeatBlobOutline,
  picture: { w: number; h: number } | null,
): Point[] => {
  const points = outlinePoints(outline);
  // The area centroid (shoelace); a degenerate outline scales about its vertex average.
  let a = 0;
  let cx = 0;
  let cy = 0;
  points.forEach((p, i) => {
    const q = points[(i + 1) % points.length];
    const cross = p.x * q.y - q.x * p.y;
    a += cross;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  });
  const centre =
    Math.abs(a) > 1e-9
      ? { x: cx / (3 * a), y: cy / (3 * a) }
      : {
          x: points.reduce((s, p) => s + p.x, 0) / points.length,
          y: points.reduce((s, p) => s + p.y, 0) / points.length,
        };
  const scaled = points.map(p => ({
    x: centre.x + (p.x - centre.x) * TAKEN_OUTLINE_SCALE,
    y: centre.y + (p.y - centre.y) * TAKEN_OUTLINE_SCALE,
  }));
  if (!picture) return scaled;
  const clipped = clipPolygonToRect(scaled, [0, 0, picture.w, picture.h]);
  // The outline lies inside the picture, so its enlargement always overlaps it; never empty.
  return clipped.length >= 3 ? clipped : scaled;
};

/** A suggestion label's line height and an average glyph width at its 12 px semibold size. */
const SUGGESTION_LABEL_LINE_PX = 14;
const SUGGESTION_LABEL_CHAR_PX = 7;
const SUGGESTION_LABEL_GAP_PX = 6;

/**
 * Where a suggestion's label goes, in canvas pixels (the SVG `<text>` baseline's left end): above
 * the outline's top point; below its bottom when there is no room above; inside the top when there
 * is room for neither. Left-aligned with the outline and pulled back inside the right and left
 * edges. With no measured canvas (`w`/`h` 0) it falls back to just above the top point.
 */
export const placeSuggestionLabel = (
  screen: Point[],
  label: string,
  canvas: { w: number; h: number },
): { x: number; y: number } => {
  const xs = screen.map(pt => pt.x);
  const ys = screen.map(pt => pt.y);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);
  const width = label.length * SUGGESTION_LABEL_CHAR_PX;
  let x = Math.min(...xs);
  if (canvas.w > 0) x = Math.max(2, Math.min(x, canvas.w - width - 2));
  const above = top - SUGGESTION_LABEL_GAP_PX;
  if (canvas.h === 0 || above - SUGGESTION_LABEL_LINE_PX >= 0) return { x, y: above };
  const below = bottom + SUGGESTION_LABEL_GAP_PX + SUGGESTION_LABEL_LINE_PX;
  if (below <= canvas.h) return { x, y: below };
  return { x, y: Math.max(top, 0) + SUGGESTION_LABEL_LINE_PX + SUGGESTION_LABEL_GAP_PX };
};

/** What a host can do to the drawing area from its own controls (the header's Undo bar, the dock). */
export interface PolygonPlotterHandle {
  /** Remove the open region's last point; the last one cancels the region. */
  undoLastPoint(): void;
  /** Drop the open region. */
  cancelRegion(): void;
  /** Fit the picture again: zoom 100 %, no pan. */
  recentre(): void;
  /**
   * Zoom and pan so a box in the picture's own pixels fills the view with a margin
   * (`observer-autogen-review-walkthrough`, R3.1). Clamped to the zoom limits; does nothing until
   * the picture's size is known.
   */
  frameTo(box: { x: number; y: number; w: number; h: number }): void;
}

export interface PolygonTip {
  title: string;
  detail: string;
  /** The entity's code, for the hover card (`observers-page-camera-stage`, FR20). */
  code?: string;
}

/** The card's "~400 ms" before a tooltip first appears. Moving region to region is immediate. */
export const REGION_TIP_DELAY_MS = 400;

/**
 * The drawing canvas's own marker, which the Observers step's focus hand-off looks for when the
 * editor opens on a camera. It replaced the retired observer tour's `data-tour` attribute, which
 * the hand-off used to borrow (`observers-page-stage`). One constant, both sides.
 */
export const OBSERVER_PLOTTER_ATTRIBUTE = 'data-observer-plotter';
/**
 * The camera view's container. The plotter's keys listen on the nearest ancestor carrying it (or
 * on the plotter itself), never on the window, so a key pressed elsewhere on the page never reaches
 * the drawing area (`observers-page-camera-stage`, FR18).
 */
export const OBSERVER_STAGE_ATTRIBUTE = 'data-observer-stage';
/**
 * Room a two-line tip needs above its region before it flips below: two caption lines plus the
 * sticker's padding and arrow. An estimate, deliberately generous — a tip flipped a few pixels early
 * costs nothing, one clipped at the canvas's top edge hides the name.
 */
const REGION_TIP_HEIGHT_PX = 48;
/** Half the card's 200 px maximum: how far from a side edge a tip's centre may sit. */
const REGION_TIP_HALF_WIDTH_PX = 100;

interface ViewState {
  scale: number;
  offsetX: number;
  offsetY: number;
}

const PolygonPlotter2 = forwardRef<PolygonPlotterHandle, Props>(function PolygonPlotter2(
  {
    className,
    imgSrc,
    onPolygonChange,
    multiPolygon = false,
    initialPolygonSet,
    framePolygons = [],
    heatImage = null,
    onNaturalSize,
    heatmapLoading = false,
    heatmapVisible = false,
    framePolygonsVisible,
    emptyMessage,
    polygonTips,
    selectedRegionId = null,
    regionsWaiting = false,
    allowDraw = true,
    drawArmed = false,
    onDrawStart,
    onStackStep,
    onPickRegion,
    onHoverRegion,
    onDeleteKey,
    onSelectRegion,
    onClearSelection,
    describedBy,
    suggestions = NO_SUGGESTIONS,
    suggestionsVisible = false,
    machineOutlines = NO_MACHINE_OUTLINES,
    heatOutlines = NO_HEAT_OUTLINES,
    onOutlineTaken,
    onOutlineHighlight,
    canvasLabel,
    onEditGesture,
    onDrawingChange,
    onZoomChange,
  },
  ref,
) {
  const showHeatmap = heatmapVisible;

  // Constants
  const LINE_HOVER_THRESHOLD = 8; // Pixel distance threshold for line hover detection
  const LONG_PRESS_DURATION_MS = 500; // Duration in milliseconds for long press to trigger delete button

  // Delete button positioning constants
  const DELETE_BUTTON_OFFSET_X = 20; // Horizontal offset from point center
  const DELETE_BUTTON_OFFSET_Y = -20; // Vertical offset from point center (negative = above)
  const DELETE_BUTTON_RADIUS = 12; // Radius of the delete button circle
  const DELETE_ICON_OFFSET = 6; // Offset to center icon within button circle

  const [polygons, setPolygons] = useState<Polygon[]>([]);

  // The loaded frame's natural size (set by handleImageLoad).
  const [imageNaturalSize, setImageNaturalSize] = useState<{ w: number; h: number } | null>(null);
  // Reported to the host, which sizes the heat image from it. Through a ref so a new callback
  // identity from the host does not re-report an unchanged size.
  const onNaturalSizeRef = useRef(onNaturalSize);
  onNaturalSizeRef.current = onNaturalSize;
  useEffect(() => {
    onNaturalSizeRef.current?.(imageNaturalSize);
  }, [imageNaturalSize]);
  const [currentPolygon, setCurrentPolygon] = useState<Polygon | null>(null);
  const [viewState, setViewState] = useState<ViewState>({ scale: 1, offsetX: 0, offsetY: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point>({ x: 0, y: 0 });
  /*
   * There is one way of working, no mode (`observer-unified-draw-edit`): a plain click selects, a
   * Shift press (or one while `drawArmed`) starts a region, and once a region is open every click
   * adds a point. Shift is read from each press, never from key state, so it cannot stick (R3.6);
   * `shiftHeld` only drives the crosshair, and is dropped when the window loses focus.
   */
  const [shiftHeld, setShiftHeld] = useState(false);
  const onDrawStartRef = useRef(onDrawStart);
  onDrawStartRef.current = onDrawStart;
  const onStackStepRef = useRef(onStackStep);
  onStackStepRef.current = onStackStep;
  /*
   * Tab held over the picture (R5.2): a click then steps through the regions under the pointer
   * instead of selecting the front one. Mirrored in a ref for the press handler, which must not
   * wait a render for a keydown that just arrived.
   */
  const [tabHeld, setTabHeldState] = useState(false);
  const tabHeldRef = useRef(false);
  const setTabHeld = useCallback((held: boolean) => {
    tabHeldRef.current = held;
    setTabHeldState(held);
  }, []);
  /** The pointer is over the picture and not over a control laid on it (R5.2). */
  const pointerOverPictureRef = useRef(false);
  /** Where the pointer last was, so a Tab pressed or released with it still can relight. */
  const lastPointerRef = useRef<{ x: number; y: number; shiftKey: boolean } | null>(null);
  /*
   * The selected region: the host's `selectedRegionId`, when it takes the selection
   * (`onSelectRegion`), so the canvas never drifts from the cards — and Delete acts on the region
   * the host shows. Without one, the plotter keeps its own.
   */
  const selectionControlled = onSelectRegion !== undefined;
  const [selectedLocal, setSelectedLocal] = useState<string | null>(null);
  const selectedPolygon = selectionControlled ? selectedRegionId : selectedLocal;
  const onSelectRegionRef = useRef(onSelectRegion);
  onSelectRegionRef.current = onSelectRegion;
  const onClearSelectionRef = useRef(onClearSelection);
  onClearSelectionRef.current = onClearSelection;
  /** A region the operator clicked — reported every time, the same region again included. */
  const selectPolygon = useCallback(
    (polygonId: string) => {
      if (selectionControlled) onSelectRegionRef.current?.(polygonId);
      else setSelectedLocal(polygonId);
    },
    [selectionControlled],
  );
  const [hoveredPoint, setHoveredPoint] = useState<{
    polygonId: string;
    pointIndex: number;
  } | null>(null);
  const [hoveredLine, setHoveredLine] = useState<{
    polygonId: string;
    lineIndex: number; // Index of the first point in the line segment
  } | null>(null);
  const [isDraggingPoint, setIsDraggingPoint] = useState(false);
  const [draggedPoint, setDraggedPoint] = useState<{
    polygonId: string;
    pointIndex: number;
  } | null>(null);
  const [showDeleteButton, setShowDeleteButton] = useState<{
    polygonId: string;
    pointIndex: number;
    x: number;
    y: number;
  } | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  // ON by default: the other observers on this camera are the first thing an operator needs to
  // see before drawing — what the camera already measures (Rob, 2026-09-24). The host's layer
  // switch hides them (`framePolygonsVisible`, `observers-page-camera-stage`).
  const showFramePolygons = framePolygonsVisible ?? true;

  const ownByArea = useMemo(() => bySmallestArea(polygons), [polygons]);
  const framesByArea = useMemo(() => bySmallestArea(framePolygons), [framePolygons]);
  /**
   * The stack at a point (`observer-unified-draw-edit`, R5.1): every finished region under it, the
   * open entity's own and — while the Observers layer is on — other entities', in the order a click
   * reaches them: smallest first, the open entity's own first on a tie (`otherIsSmaller`), then the
   * later region (`bySmallestArea`). A plain click takes the first; Tab+click steps through the rest.
   */
  const regionsAt = useCallback(
    (point: Point): StackEntry[] => {
      const inside = (p: Polygon) =>
        p.isComplete && p.points.length >= 3 && pointInPolygon(point, p.points);
      const own = ownByArea.filter(inside);
      const other = showFramePolygons ? framesByArea.filter(inside) : [];
      const stack: StackEntry[] = [];
      let i = 0;
      let j = 0;
      while (i < own.length || j < other.length) {
        if (j >= other.length || (i < own.length && !otherIsSmaller(own[i], other[j]))) {
          stack.push({ id: own[i].id, own: true, polygon: own[i] });
          i += 1;
        } else {
          stack.push({ id: other[j].id, own: false, polygon: other[j] });
          j += 1;
        }
      }
      return stack;
    },
    [ownByArea, showFramePolygons, framesByArea],
  );
  /**
   * The stack a Tab+click steps through: another entity's region counts only where a click could
   * pick it (`onPickRegion`), as a plain click there would.
   */
  const steppableAt = useCallback(
    (point: Point): StackEntry[] => regionsAt(point).filter(entry => entry.own || !!onPickRegion),
    [regionsAt, onPickRegion],
  );
  /** The open entity's region a point is inside, smallest first — what a click there selects. */
  const ownRegionAt = useCallback(
    (point: Point): Polygon | null => regionsAt(point).find(entry => entry.own)?.polygon ?? null,
    [regionsAt],
  );
  /**
   * What a click inside the regions at a point reaches: the front of the stack, the open entity's own
   * (selected on release) or another entity's (picked). Corners and edges are asked first, by the
   * caller.
   */
  const insideAt = useCallback(
    (point: Point): { own: Polygon | null; otherId: string | null } => {
      const [front] = regionsAt(point);
      if (!front) return { own: null, otherId: null };
      return front.own ? { own: front.polygon, otherId: null } : { own: null, otherId: front.id };
    },
    [regionsAt],
  );
  const [isRealImage, setIsRealImage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedPointForKeyboard, setSelectedPointForKeyboard] = useState<{
    polygonId: string;
    pointIndex: number;
  } | null>(null);

  /*
   * The reshape in progress — a corner drag, or a run of arrow-key nudges of the selected corner —
   * bracketed by `onEditGesture`, so the host records each as ONE undo step (FR16): key auto-repeat
   * would otherwise fill the 50-entry history from a single held arrow. Exactly one `end` follows
   * every `start`, whatever cuts the gesture short.
   */
  const onEditGestureRef = useRef(onEditGesture);
  onEditGestureRef.current = onEditGesture;
  const gestureRef = useRef<'drag' | 'nudge' | null>(null);
  const endGesture = useCallback(() => {
    if (!gestureRef.current) return;
    gestureRef.current = null;
    onEditGestureRef.current?.('end');
  }, []);
  const beginGesture = useCallback(
    (kind: 'drag' | 'nudge') => {
      if (gestureRef.current === kind) return;
      endGesture();
      gestureRef.current = kind;
      onEditGestureRef.current?.('start');
    },
    [endGesture],
  );
  const endNudge = useCallback(() => {
    if (gestureRef.current === 'nudge') endGesture();
  }, [endGesture]);
  // A different corner selected (or none) ends a run of nudges; unmounting ends any gesture.
  useEffect(
    () => endNudge,
    [selectedPointForKeyboard?.polygonId, selectedPointForKeyboard?.pointIndex, endNudge],
  );
  useEffect(() => endGesture, [endGesture]);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  /** The plotter's outermost element — the keyboard root when no camera stage contains it. */
  const outerRef = useRef<HTMLDivElement>(null);

  /*
   * Heat outlines (`observer-matching-beyond-geometry-heat-outlines`): drawn only while the heat
   * shows on a real picture. The highlight is the outline `O` reached, for `Enter`; new
   * outlines (a slider move, another camera or window) start with none.
   */
  const shownHeatOutlines =
    showHeatmap && isRealImage && imageLoaded ? heatOutlines : NO_HEAT_OUTLINES;
  const [outlineHighlight, setOutlineHighlight] = useState<number | null>(null);
  useEffect(() => setOutlineHighlight(null), [heatOutlines]);
  /*
   * An outline is taken only where a hand-drawn region could be closed: drawing allowed here, and a
   * free slot where only one region is held (a waiting region already there takes the slot). The
   * draw path's own rule (`allowDraw`, `canDrawPolygon`), so a take can never make a region the
   * host has refused.
   */
  // Not while Add Observer is armed: that is a region to be drawn by hand, and a take would leave
  // the tool armed for the next click.
  const canTakeOutline = allowDraw && !drawArmed && (multiPolygon || polygons.length === 0);
  /**
   * A closed region, hand-drawn or taken from a heat outline: one path, so the host receives both
   * the same way.
   */
  const closeRegion = useCallback(
    (completed: Polygon) => {
      setPolygons(prev => [...prev, completed]);
      setCurrentPolygon(null);
      onPolygonChange?.([...polygons, completed]);
      // Closed, it is selected straight away, so the next drag adjusts it (R3.4). A region
      // waiting for its entity (single-polygon) is not an observer yet: nothing is selected.
      if (multiPolygon) selectPolygon(completed.id);
    },
    [polygons, onPolygonChange, multiPolygon, selectPolygon],
  );
  /** Takes an outline as a region; false where the draw rule refuses it. */
  const takeOutline = (index: number): boolean => {
    const outline = shownHeatOutlines[index];
    if (!outline || !canTakeOutline) return false;
    // A corner chosen for the arrow keys belongs to the region left behind.
    setSelectedPointForKeyboard(null);
    closeRegion({
      id: `polygon_${Date.now()}`,
      // Taken with a margin round the heat (heat-outline-margin); the outline drawn is unchanged.
      points: enlargedOutlinePoints(outline, imageNaturalSize),
      isComplete: true,
    });
    setOutlineHighlight(null);
    onOutlineTaken?.();
    return true;
  };
  // Read at event time by the press and key handlers, which outlive this render.
  const takeOutlineRef = useRef(takeOutline);
  takeOutlineRef.current = takeOutline;
  const onOutlineHighlightRef = useRef(onOutlineHighlight);
  onOutlineHighlightRef.current = onOutlineHighlight;
  const heatOutlineAt = (point: Point) =>
    shownHeatOutlines.findIndex(o => pointInPolygon(point, outlinePoints(o)));
  const heatOutlineAtRef = useRef(heatOutlineAt);
  heatOutlineAtRef.current = heatOutlineAt;
  /*
   * `O` moves the highlight through the outlines, largest first (Shift+`O` back), and `Enter` takes
   * it (spec R6): the keyboard path to what a click does. Not while a region is being drawn, never
   * from a control laid over the picture. (`[` is the step's camera-list key, so it is left alone.)
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container || shownHeatOutlines.length === 0 || currentPolygon || !canTakeOutline) return;
    const handleKey = (e: KeyboardEvent) => {
      if ((e.target as Element | null)?.closest?.('[data-control]')) return;
      const count = shownHeatOutlines.length;
      if ((e.key === 'o' || e.key === 'O') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        const step = e.shiftKey ? -1 : 1;
        const next =
          outlineHighlight === null
            ? step === 1
              ? 0
              : count - 1
            : (outlineHighlight + step + count) % count;
        setOutlineHighlight(next);
        onOutlineHighlightRef.current?.(next + 1, count);
      } else if (e.key === 'Enter' && outlineHighlight !== null) {
        e.preventDefault();
        e.stopPropagation();
        takeOutlineRef.current(outlineHighlight);
      }
    };
    container.addEventListener('keydown', handleKey);
    return () => container.removeEventListener('keydown', handleKey);
  }, [shownHeatOutlines, currentPolygon, outlineHighlight, canTakeOutline]);
  /*
   * A press that is not yet a click or a pan (#1865, FR12): it becomes a pan once the pointer
   * travels past `DRAG_THRESHOLD_PX`, and a click if it never does. What the click does was decided
   * at the press (`observer-unified-draw-edit`): `draw` starts a region on release (R3.2), `select`
   * selects or deselects (R2.1). So dragging the picture never drops a point.
   */
  const pressRef = useRef<{
    x: number;
    y: number;
    point: Point;
    moved: boolean;
    intent: 'draw' | 'select';
  } | null>(null);
  /*
   * What the last press was for — read by the regions' own `onClick`s, which fire after the release:
   * only a press that selects may also select a corner for the keys or add one on an edge. Without
   * it, a Shift press on an edge would both start a region and add a corner.
   */
  const pressIntentRef = useRef<'draw' | 'select' | 'add' | 'step' | null>(null);
  /** Whether the click now arriving may select or add a corner: read once, then forgotten. */
  const clickSelects = (): boolean => {
    const intent = pressIntentRef.current;
    pressIntentRef.current = null;
    return intent === null || intent === 'select';
  };
  /** The backdrop hit test, read through a ref: it is defined further down than the handlers. */
  const backdropRegionAtRef = useRef<(clientX: number, clientY: number) => string | null>(
    () => null,
  );

  // The canvas's own size, so the picture can be FITTED to it rather than drawn at natural size
  // and capped: CSS `max-width/max-height: 100%` never upscales a small frame and needs a
  // resolved percentage height, which a deep flex chain does not always give it. Measured on the
  // observer workspace, where a stored frame rendered at natural size and was clipped.
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  // A callback ref rather than a mount effect: should the Container ever be re-created (a different
  // element), an observer attached once at mount would keep watching the dead one. Assigning
  // `containerRef.current` here keeps every other reader of it working.
  const setContainerNode = useCallback((el: HTMLDivElement | null) => {
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      // The LAYOUT box, not getBoundingClientRect: the latter is scaled by any ancestor transform,
      // and the design-system Modal enters through a 0.96 → 1 scale animation — measured, the
      // observer fired during it and the picture fitted 4% short for the life of the editor.
      const w = el.clientWidth;
      const h = el.clientHeight;
      setContainerSize(prev => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    resizeObserverRef.current = observer;
  }, []);
  const fitted =
    containerSize && imageNaturalSize ? fitInto(containerSize, imageNaturalSize) : null;
  /*
   * The overlay (polygons, other observers, heat) is laid out from the picture's LIVE box
   * (`getScreenCoordinates` reads `getBoundingClientRect`), which on the render that changes the
   * layout still holds the OLD box: the fitted size, the canvas size and the zoom/pan transform only
   * reach the picture when that render commits. So the regions were drawn against the old picture —
   * one wheel notch or pan step behind, or the pre-fit size — while the picture painted at the new
   * one. A layout effect runs after the commit and before paint; the re-render it asks for measures
   * the committed box afresh (the box cache lives one render), and the operator sees one frame.
   */
  const [, overlayCommitted] = useReducer((n: number) => n + 1, 0);
  useLayoutEffect(() => {
    if (imageRef.current) overlayCommitted();
  }, [fitted?.w, fitted?.h, containerSize?.w, containerSize?.h, viewState]);

  // Load multiple polygons from initialPolygonSet — replaces polygons whenever the prop changes
  useEffect(() => {
    if (initialPolygonSet) {
      setPolygons(initialPolygonSet);
      /*
       * Seeded with finished regions, the plotter edits them — their corners can be dragged and
       * their edges take new points (#1865's review fix). A Shift press starts a new region.
       */
      // A reseed mid-gesture (a drag, a run of nudges) ends it: the regions it reshaped are gone.
      endGesture();
      setIsDraggingPoint(false);
      setDraggedPoint(null);
      setIsDragging(false);
      pressRef.current = null;
      // A long-press delete button, shown or pending, belongs to a corner of the regions just replaced.
      // Dropping the timer's state cancels it (the cleanup effect on `longPressTimer`).
      setLongPressTimer(null);
      setShowDeleteButton(null);
      setSelectedLocal(null);
      setCurrentPolygon(null);
      setSelectedPointForKeyboard(null);
      setHoveredPoint(null);
      setHoveredLine(null);
      onPolygonChange?.(initialPolygonSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `initialPolygonSet` only: a reseed resets the editor, nothing else should
  }, [initialPolygonSet]);

  // Track when we have a real image vs TV noise, and reset state when image changes
  const prevImgSrcRef = useRef<string | null>(null);
  useEffect(() => {
    const hasRealImage = !!(imgSrc && imgSrc !== '/tv-noise.png' && imgSrc.trim() !== '');
    setIsRealImage(hasRealImage);
    if (!hasRealImage) {
      setImageLoaded(false);
      setImageNaturalSize(null);
    }

    // If the image source changed (not just on initial mount): the regions stay (see the props),
    // and what belonged to the previous picture goes.
    if (prevImgSrcRef.current !== null && prevImgSrcRef.current !== imgSrc) {
      // Always drop any in-progress draw: it was anchored to the previous
      // frame, so carrying it over would let subsequent clicks append points to
      // a polygon that started on a different source.
      setCurrentPolygon(null);
      setImageNaturalSize(null);
    }
    prevImgSrcRef.current = imgSrc;
  }, [imgSrc]);

  // Handle image load events
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const isRealImageSrc = img.src && !img.src.includes('/tv-noise.png') && img.src.trim() !== '';

    if (isRealImageSrc) {
      setImageLoaded(true);
      // Cache natural dimensions (reported to the host, which sizes the heat image from them)
      // rather than reading the ref (refs don't trigger re-renders).
      setImageNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    } else {
      setImageLoaded(false);
      setImageNaturalSize(null);
    }
  }, []);

  // Check if drawing should be allowed - now includes real image check
  const canDrawPolygon = (multiPolygon || polygons.length === 0) && isRealImage && imageLoaded;
  /** Whether a press with Shift as given would start a region here (R3.2, R3.5, R4.2). */
  const pressDraws = useCallback(
    (shiftKey: boolean): boolean => (shiftKey || drawArmed) && allowDraw && canDrawPolygon,
    [drawArmed, allowDraw, canDrawPolygon],
  );

  // Keyboard accessibility for point movement
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedPointForKeyboard) return;
      // A control laid over the picture (the tracking grip) owns its own arrows (#1865 review).
      if ((e.target as Element | null)?.closest?.('[data-control]')) return;

      const { polygonId, pointIndex } = selectedPointForKeyboard;
      const currentPolygon = polygons.find(p => p.id === polygonId);
      if (!currentPolygon || pointIndex >= currentPolygon.points.length) return;

      const currentPoint = currentPolygon.points[pointIndex];
      const moveDistance = e.shiftKey ? 10 : 1; // Larger steps with Shift key
      let newX = currentPoint.x;
      let newY = currentPoint.y;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newX = Math.max(0, currentPoint.x - moveDistance);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newX = currentPoint.x + moveDistance;
          break;
        case 'ArrowUp':
          e.preventDefault();
          newY = Math.max(0, currentPoint.y - moveDistance);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newY = currentPoint.y + moveDistance;
          break;
        case 'Escape':
          e.preventDefault();
          // Consumed here — the selected corner was the thing to cancel (Escape is layered).
          e.stopPropagation();
          setSelectedPointForKeyboard(null);
          return;
        default:
          return;
      }
      // A corner key was consumed: the stage's own listener must not also pan or act on it.
      e.stopPropagation();
      // The first nudge of a run opens the gesture; the key's release closes it.
      beginGesture('nudge');

      // Update the polygon with the new point position
      const updatedPolygons = polygons.map(polygon => {
        if (polygon.id === polygonId) {
          const newPoints = [...polygon.points];
          newPoints[pointIndex] = { x: newX, y: newY };
          return { ...polygon, points: newPoints };
        }
        return polygon;
      });

      setPolygons(updatedPolygons);
      onPolygonChange?.(updatedPolygons);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key.startsWith('Arrow')) endNudge();
    };

    if (selectedPointForKeyboard && containerRef.current) {
      // Add keyboard event listener to the container to ensure focus
      const container = containerRef.current;
      container.addEventListener('keydown', handleKeyDown);
      container.addEventListener('keyup', handleKeyUp);
      container.focus();

      return () => {
        container.removeEventListener('keydown', handleKeyDown);
        container.removeEventListener('keyup', handleKeyUp);
      };
    }
  }, [selectedPointForKeyboard, polygons, onPolygonChange, beginGesture, endNudge]);

  // Cleanup long press timer on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  const getImageCoordinates = useCallback(
    (clientX: number, clientY: number): Point | null => {
      if (!containerRef.current || !imageRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      const img = imageRef.current;

      // Get click position relative to container
      const containerX = clientX - rect.left;
      const containerY = clientY - rect.top;

      // Check if the click is within the image bounds
      const imgRect = img.getBoundingClientRect();
      const imgContainerX = imgRect.left - rect.left;
      const imgContainerY = imgRect.top - rect.top;
      const imgWidth = imgRect.width;
      const imgHeight = imgRect.height;

      if (
        containerX < imgContainerX ||
        containerY < imgContainerY ||
        containerX > imgContainerX + imgWidth ||
        containerY > imgContainerY + imgHeight
      ) {
        return null;
      }

      // Convert to natural image coordinates
      const naturalX = ((containerX - imgContainerX) / imgWidth) * img.naturalWidth;
      const naturalY = ((containerY - imgContainerY) / imgHeight) * img.naturalHeight;

      return { x: naturalX, y: naturalY };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `viewState`: zoom/pan moves the image box this reads
    [viewState],
  );

  /*
   * The two boxes every point is placed by, read once per RENDER rather than twice per point: a
   * zoom places every region's corners and the layers' transform, and each read was a layout query
   * (FR28). Dropped at the top of every render, so a read never outlives the layout it measured;
   * the re-render the overlay layout effect asks for after a view change, a fit or a resize measures
   * the committed box. Event handlers between renders reuse the last render's — the committed — one.
   */
  const boxesRef = useRef<{ rect: DOMRect; imgRect: DOMRect } | null>(null);
  boxesRef.current = null;
  const getScreenCoordinates = useCallback(
    (imageX: number, imageY: number): Point => {
      if (!containerRef.current || !imageRef.current) return { x: 0, y: 0 };

      const img = imageRef.current;
      let boxes = boxesRef.current;
      if (!boxes) {
        boxes = {
          rect: containerRef.current.getBoundingClientRect(),
          imgRect: img.getBoundingClientRect(),
        };
        boxesRef.current = boxes;
      }
      const { rect, imgRect } = boxes;

      // Convert from natural image coordinates to screen coordinates
      const imgContainerX = imgRect.left - rect.left;
      const imgContainerY = imgRect.top - rect.top;
      const imgWidth = imgRect.width;
      const imgHeight = imgRect.height;

      const relativeX = (imageX / img.naturalWidth) * imgWidth;
      const relativeY = (imageY / img.naturalHeight) * imgHeight;

      return {
        x: imgContainerX + relativeX,
        y: imgContainerY + relativeY,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `viewState`: zoom/pan moves the image box this reads
    [viewState],
  );

  /**
   * Calculates the shortest distance from a point to a line segment using vector projection.
   *
   * Algorithm:
   * 1. Projects the vector from line start to point onto the line segment vector
   * 2. Clamps the projection parameter to [0,1] to handle endpoints
   * 3. Finds the closest point on the line segment and calculates Euclidean distance
   */
  const distanceToLineSegment = useCallback(
    (point: Point, lineStart: Point, lineEnd: Point): number => {
      // Vector from line start to the test point
      const pointDeltaX = point.x - lineStart.x;
      const pointDeltaY = point.y - lineStart.y;

      // Vector representing the line segment
      const lineDeltaX = lineEnd.x - lineStart.x;
      const lineDeltaY = lineEnd.y - lineStart.y;

      // Dot product of the two vectors (projection scalar)
      const dot = pointDeltaX * lineDeltaX + pointDeltaY * lineDeltaY;
      // Squared length of the line segment
      const lenSq = lineDeltaX * lineDeltaX + lineDeltaY * lineDeltaY;

      // Projection parameter: how far along the line segment the closest point lies
      let projectionParameter = -1;
      if (lenSq !== 0) {
        projectionParameter = dot / lenSq; // Normalized parameter [0,1] for points on the segment
      }

      let closestX, closestY;

      // Clamp parameter to [0,1] to ensure closest point is on the line segment
      if (projectionParameter < 0) {
        // Closest point is the line start
        closestX = lineStart.x;
        closestY = lineStart.y;
      } else if (projectionParameter > 1) {
        // Closest point is the line end
        closestX = lineEnd.x;
        closestY = lineEnd.y;
      } else {
        // Closest point is on the line segment
        closestX = lineStart.x + projectionParameter * lineDeltaX;
        closestY = lineStart.y + projectionParameter * lineDeltaY;
      }

      // Calculate Euclidean distance from point to closest point on segment
      const dx = point.x - closestX;
      const dy = point.y - closestY;
      return Math.sqrt(dx * dx + dy * dy);
    },
    [],
  );

  // Function to find the closest line segment to a screen point
  const findClosestLineSegment = useCallback(
    (
      screenX: number,
      screenY: number,
    ): {
      polygonId: string;
      lineIndex: number;
      distance: number;
    } | null => {
      let closestLine: { polygonId: string; lineIndex: number; distance: number } | null = null;

      // Smallest first, as they paint: on a shared edge the one drawn in front takes the corner.
      for (const polygon of ownByArea) {
        for (let i = 0; i < polygon.points.length; i++) {
          const currentPoint = polygon.points[i];
          const nextPoint = polygon.points[(i + 1) % polygon.points.length];

          const currentScreen = getScreenCoordinates(currentPoint.x, currentPoint.y);
          const nextScreen = getScreenCoordinates(nextPoint.x, nextPoint.y);

          const containerRect = containerRef.current?.getBoundingClientRect();
          if (!containerRect) continue;

          const adjustedScreenX = screenX - containerRect.left;
          const adjustedScreenY = screenY - containerRect.top;

          const distance = distanceToLineSegment(
            { x: adjustedScreenX, y: adjustedScreenY },
            currentScreen,
            nextScreen,
          );

          if (
            distance < LINE_HOVER_THRESHOLD &&
            (!closestLine || distance < closestLine.distance)
          ) {
            closestLine = { polygonId: polygon.id, lineIndex: i, distance };
          }
        }
      }

      return closestLine;
    },
    [ownByArea, getScreenCoordinates, distanceToLineSegment],
  );

  const insertPoint = useCallback(
    (polygonId: string, lineIndex: number, newPoint: Point) => {
      const updatedPolygons = polygons.map(polygon => {
        if (polygon.id === polygonId) {
          const newPoints = [...polygon.points];
          // Insert the new point after the lineIndex
          newPoints.splice(lineIndex + 1, 0, newPoint);
          return { ...polygon, points: newPoints };
        }
        return polygon;
      });

      setPolygons(updatedPolygons);
      onPolygonChange?.(updatedPolygons);
    },
    [polygons, onPolygonChange],
  );

  // Helper function to determine which line segment was clicked
  const getClickedLineSegment = useCallback(
    (polygon: Polygon, clickX: number, clickY: number): number | null => {
      if (polygon.points.length < 2) return null;

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return null;

      const adjustedX = clickX - containerRect.left;
      const adjustedY = clickY - containerRect.top;

      let closestSegment: { index: number; distance: number } | null = null;

      for (let i = 0; i < polygon.points.length; i++) {
        const currentPoint = polygon.points[i];
        const nextPoint = polygon.points[(i + 1) % polygon.points.length];

        // Skip the closing line for incomplete polygons
        if (!polygon.isComplete && i === polygon.points.length - 1) break;

        const currentScreen = getScreenCoordinates(currentPoint.x, currentPoint.y);
        const nextScreen = getScreenCoordinates(nextPoint.x, nextPoint.y);

        const distance = distanceToLineSegment(
          { x: adjustedX, y: adjustedY },
          currentScreen,
          nextScreen,
        );

        if (
          distance < LINE_HOVER_THRESHOLD &&
          (!closestSegment || distance < closestSegment.distance)
        ) {
          closestSegment = { index: i, distance };
        }
      }

      return closestSegment?.index ?? null;
    },
    [getScreenCoordinates, distanceToLineSegment],
  );

  /**
   * The open entity's corner, else its edge, within reach of a pointer at (clientX, clientY) — what
   * the hover marks and a plain press acts on. Computed from the pointer rather than read from the
   * hover state, so the backdrop hover can ask in the same move that sets it (`observer-overlap-pick`,
   * FR4).
   */
  const ownControlAt = useCallback(
    (
      clientX: number,
      clientY: number,
    ): {
      point: { polygonId: string; pointIndex: number } | null;
      line: { polygonId: string; lineIndex: number } | null;
    } => {
      const rect = containerRef.current?.getBoundingClientRect();
      // Smallest first, as they paint: on a shared corner the handle drawn in front is the one taken.
      for (const polygon of ownByArea) {
        for (let i = 0; i < polygon.points.length; i++) {
          const p = polygon.points[i];
          const screenP = getScreenCoordinates(p.x, p.y);
          const distance = Math.sqrt(
            Math.pow(clientX - (rect?.left || 0) - screenP.x, 2) +
              Math.pow(clientY - (rect?.top || 0) - screenP.y, 2),
          );
          if (distance < 10) {
            return { point: { polygonId: polygon.id, pointIndex: i }, line: null };
          }
        }
      }
      const closestLine = findClosestLineSegment(clientX, clientY);
      return {
        point: null,
        line: closestLine
          ? { polygonId: closestLine.polygonId, lineIndex: closestLine.lineIndex }
          : null,
      };
    },
    [ownByArea, getScreenCoordinates, findClosestLineSegment],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Check if the click is on a control element (button, etc.)
      const target = e.target as HTMLElement;
      if (target.closest('[data-control]')) {
        return; // Don't handle clicks on control elements
      }

      // Check if clicking on delete button
      if (target.closest('[data-delete-button]')) {
        return; // Let the delete button handle its own click
      }

      // Don't handle mouse interactions when showing TV noise
      if (!isRealImage || !imageLoaded) {
        return;
      }

      e.preventDefault();
      /*
       * The default just prevented is also the one that moves focus to the canvas, so take it here:
       * the picture's keys — Tab held for Tab+click (`observer-unified-draw-edit`, R5.2), the arrows,
       * Delete — listen on the stage the canvas sits in, and must reach it after a click on the
       * picture even when focus was last in the list.
       */
      containerRef.current?.focus({ preventScroll: true });

      // Only handle left clicks; a Ctrl-press on a corner is macOS's right-click (#1913)
      if (e.button !== 0 || (e.ctrlKey && (e.target as Element).tagName === 'circle')) return;

      // Hide delete button if clicking elsewhere
      setShowDeleteButton(null);

      // Get coordinates relative to the image
      const point = getImageCoordinates(e.clientX, e.clientY);

      /*
       * Tab held: the press steps through the regions under the pointer (R5.3, R5.4) — never a corner
       * drag, an edge's new corner or a new region. Shift steps the other way. The result is selected
       * as a plain click on it would be (R5.5): the open entity's own region selected, another
       * entity's picked. With a region open or Add Observer armed, Tab is not held (R5.7).
       */
      if (tabHeldRef.current && !currentPolygon && !drawArmed) {
        pressIntentRef.current = 'step';
        if (!point) return;
        const stack = steppableAt(point);
        const next = stepInStack(stack, selectedPolygon, e.shiftKey);
        if (!next) return;
        if (next.own) selectPolygon(next.id);
        else onPickRegion?.(next.id);
        onStackStepRef.current?.(next.id, stack.indexOf(next) + 1, stack.length);
        return;
      }

      // A region open: every press adds a point, with or without Shift, and one near the first
      // point (3 or more placed) closes it (`observer-unified-draw-edit`, R3.3).
      if (currentPolygon) {
        pressIntentRef.current = 'add';
        if (!point) return;
        const firstPoint = currentPolygon.points[0];
        const distance = Math.sqrt(
          Math.pow(point.x - firstPoint.x, 2) + Math.pow(point.y - firstPoint.y, 2),
        );
        if (distance < 20 / viewState.scale && currentPolygon.points.length >= 3) {
          closeRegion({ ...currentPolygon, isComplete: true });
        } else {
          setCurrentPolygon(prev => (prev ? { ...prev, points: [...prev.points, point] } : null));
        }
        return;
      }

      /*
       * Shift held, or Add Observer armed, where drawing is allowed: this press starts a region on
       * release, wherever it lands — on empty picture, inside any region, on a corner or an edge. It
       * never selects, picks or drags (R3.2). Read from this event, never from remembered key state.
       */
      /*
       * A Shift press never selects, picks or drags. Where drawing is not allowed here (R3.5) it does
       * nothing at all — the release starts no region, though a press that travels still pans — so
       * a Shift+click over another entity's region cannot pick it (and so link a waiting region).
       */
      const drawIntent = e.shiftKey || pressDraws(false);

      // Pick a region on the picture: the backdrop region under the pointer — the one whose
      // outline is lit — is what a click picks (#1865, FR9). Not on a press that draws: that one is
      // a corner of a new region wherever it lands, even over another entity's region (a whole-floor
      // region covers every picture).
      // The open entity's corners and edges come first (`observer-overlap-pick`, FR1): a press on one
      // drags it or adds a corner, never a pick of the region underneath. Inside, the smallest region
      // under the pointer wins, own or another entity's (ruling 2026-10-04): the treadmill inside the
      // open zone is still picked, and the zone around the open treadmill still is.
      // Asked of the pointer now, not of the last move's hover: a zoom, a pan or a keyboard nudge
      // moves the picture under a still pointer.
      const { point: pressedPoint, line: pressedLine } =
        !drawIntent && point ? ownControlAt(e.clientX, e.clientY) : { point: null, line: null };
      if (onPickRegion && !drawIntent && point && !pressedPoint && !pressedLine) {
        const { otherId } = insideAt(point);
        if (otherId) {
          pressIntentRef.current = 'select';
          onPickRegion(otherId);
          return;
        }
      }

      pressIntentRef.current = drawIntent ? 'draw' : 'select';
      // Off a corner and an edge: wait to see whether this is a drag or a click.
      if (!pressedPoint && !pressedLine && point) {
        pressRef.current = {
          x: e.clientX,
          y: e.clientY,
          point,
          moved: false,
          intent: drawIntent ? 'draw' : 'select',
        };
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        return;
      }

      if (pressedPoint && point) {
        // Clear any existing timer
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          setLongPressTimer(null);
        }

        // Start long press timer for delete button
        // Cache polygon lookup and point coordinates to avoid repeated find operations
        const polygon = polygons.find(p => p.id === pressedPoint.polygonId);
        const point = polygon?.points[pressedPoint.pointIndex];

        if (point) {
          // Capture the current hovered point to prevent race conditions
          const capturedHoveredPoint = { ...pressedPoint };

          const timer = setTimeout(() => {
            // Validate that the timer is still relevant (no race condition)
            // Check if the currently hovered point matches what was captured
            if (
              pressedPoint &&
              pressedPoint.polygonId === capturedHoveredPoint.polygonId &&
              pressedPoint.pointIndex === capturedHoveredPoint.pointIndex
            ) {
              const screenPoint = getScreenCoordinates(point.x, point.y);
              setShowDeleteButton({
                polygonId: capturedHoveredPoint.polygonId,
                pointIndex: capturedHoveredPoint.pointIndex,
                x: screenPoint.x,
                y: screenPoint.y,
              });
            }
            // If validation fails, do nothing (prevents stale delete button)
          }, LONG_PRESS_DURATION_MS);
          setLongPressTimer(timer);
        }

        // Start dragging point — one edit, from here to the release.
        beginGesture('drag');
        setIsDraggingPoint(true);
        setDraggedPoint(pressedPoint);
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
      }

      // Handle line clicks for adding new points
      if (!pressedPoint && pressedLine && point) {
        insertPoint(pressedLine.polygonId, pressedLine.lineIndex, point);
      }
    },
    [
      currentPolygon,
      getImageCoordinates,
      getScreenCoordinates,
      viewState.scale,
      insertPoint,
      polygons,
      pressDraws,
      drawArmed,
      isRealImage,
      imageLoaded,
      longPressTimer,
      onPickRegion,
      beginGesture,
      ownControlAt,
      insideAt,
      steppableAt,
      selectPolygon,
      selectedPolygon,
      closeRegion,
    ],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const press = pressRef.current;
      if (press && e.buttons === 0) {
        // The button came up outside the canvas, where no mouseup reached it: the press is over,
        // and the picture must not keep following a pointer nothing is holding (#1865 review).
        pressRef.current = null;
        setIsDragging(false);
        return;
      }
      if (press) {
        if (
          !press.moved &&
          Math.hypot(e.clientX - press.x, e.clientY - press.y) > DRAG_THRESHOLD_PX
        ) {
          press.moved = true;
        }
        if (press.moved) {
          const deltaX = e.clientX - dragStart.x;
          const deltaY = e.clientY - dragStart.y;
          setViewState(prev => ({
            ...prev,
            offsetX: prev.offsetX + deltaX,
            offsetY: prev.offsetY + deltaY,
          }));
          setDragStart({ x: e.clientX, y: e.clientY });
        }
        return;
      }
      // Don't handle mouse move when showing TV noise
      if (!isRealImage || !imageLoaded) return;

      // Handle point dragging
      if (isDraggingPoint && draggedPoint) {
        // Cancel long press timer if user moves mouse while dragging
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          setLongPressTimer(null);
        }

        const newPoint = getImageCoordinates(e.clientX, e.clientY);
        if (newPoint) {
          const updatedPolygons = polygons.map(polygon => {
            if (polygon.id === draggedPoint.polygonId) {
              const newPoints = [...polygon.points];
              newPoints[draggedPoint.pointIndex] = newPoint;
              return { ...polygon, points: newPoints };
            }
            return polygon;
          });
          setPolygons(updatedPolygons);
          onPolygonChange?.(updatedPolygons);
        }
        return;
      }

      // No corner or edge is marked where a press would not take it: a region open, or a press
      // that would draw (R3.1).
      const point = getImageCoordinates(e.clientX, e.clientY);
      const drawNow = pressDraws(e.shiftKey);
      if (!point || currentPolygon || drawNow || tabHeldRef.current) {
        setHoveredPoint(null);
        setHoveredLine(null);
        return;
      }

      const { point: foundPoint, line } = ownControlAt(e.clientX, e.clientY);
      setHoveredPoint(foundPoint);
      setHoveredLine(line);
    },
    [
      isDraggingPoint,
      draggedPoint,
      currentPolygon,
      pressDraws,
      dragStart,
      getImageCoordinates,
      ownControlAt,
      polygons,
      onPolygonChange,
      isRealImage,
      imageLoaded,
      longPressTimer,
    ],
  );

  const handleMouseUp = useCallback(() => {
    // A press that never travelled is a click; one that did was a pan, and is simply over.
    const press = pressRef.current;
    if (press) {
      pressRef.current = null;
      setIsDragging(false);
      if (press.moved) return;
      // A press that draws places the first point of a new region (`observer-unified-draw-edit`,
      // R3.2). A plain click never does, wherever it lands (R2.2).
      if (press.intent === 'draw') {
        if (allowDraw && canDrawPolygon) {
          setSelectedLocal(null);
          // A corner chosen for the keys belongs to the region left behind: the arrows and Esc now
          // act on the region being drawn.
          setSelectedPointForKeyboard(null);
          setCurrentPolygon({
            id: `polygon_${Date.now()}`,
            points: [press.point],
            isComplete: false,
          });
          onDrawStartRef.current?.();
        }
        return;
      }
      // A click inside one of the open entity's regions selects it, smallest first
      // (`observer-overlap-pick`, FR2); a click on empty picture — over no region at all — deselects.
      const own = ownRegionAt(press.point);
      if (own) {
        selectPolygon(own.id);
      } else if (!backdropRegionAtRef.current(press.x, press.y)) {
        // Over no region, a click inside a heat outline takes it as a region.
        const outline = heatOutlineAtRef.current(press.point);
        if (outline >= 0 && takeOutlineRef.current(outline)) return;
        setSelectedLocal(null);
        setSelectedPointForKeyboard(null);
        onClearSelectionRef.current?.();
      }
      return;
    }
    // Clear long press timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    setIsDragging(false);
    if (gestureRef.current === 'drag') endGesture();
    setIsDraggingPoint(false);
    setDraggedPoint(null);
  }, [endGesture, longPressTimer, allowDraw, canDrawPolygon, ownRegionAt, selectPolygon]);

  /** Zoom by `factor` about a point in the canvas (px), keeping that point under the pointer. */
  const zoomAbout = useCallback((factor: number, canvasX: number, canvasY: number) => {
    // The transform's origin is the picture's own corner, which the canvas centres with a
    // letterbox margin — so the point is taken relative to it, or zoom drifts by that margin.
    const atX = canvasX - (imageRef.current?.offsetLeft ?? 0);
    const atY = canvasY - (imageRef.current?.offsetTop ?? 0);
    setViewState(prev => {
      const scale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev.scale * factor));
      const change = scale / prev.scale;
      return {
        scale,
        offsetX: atX - (atX - prev.offsetX) * change,
        offsetY: atY - (atY - prev.offsetY) * change,
      };
    });
  }, []);

  /*
   * The mouse wheel zooms about the cursor (#1865, FR12). A native listener, because React attaches
   * `onWheel` passively and the page would scroll under the picture as it zoomed.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if ((e.target as Element | null)?.closest?.('[data-control]')) return;
      // A purely sideways scroll is no zoom request.
      if (e.deltaY === 0) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomAbout(
        e.deltaY < 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP,
        e.clientX - rect.left,
        e.clientY - rect.top,
      );
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAbout]);

  /** The keys zoom about the canvas centre — the same maths as the wheel. */
  const zoomBy = useCallback(
    (factor: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) zoomAbout(factor, rect.width / 2, rect.height / 2);
    },
    [zoomAbout],
  );
  const zoomIn = useCallback(() => zoomBy(1.2), [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(0.8), [zoomBy]);

  const deletePolygon = useCallback(
    (polygonId: string) => {
      const updatedPolygons = polygons.filter(p => p.id !== polygonId);
      setPolygons(updatedPolygons);
      onPolygonChange?.(updatedPolygons);
      setSelectedLocal(prev => (prev === polygonId ? null : prev));
    },
    [polygons, onPolygonChange],
  );

  const resetView = useCallback(() => {
    setViewState({ scale: 1, offsetX: 0, offsetY: 0 });
  }, []);

  const frameTo = useCallback(
    (box: { x: number; y: number; w: number; h: number }) => {
      if (!fitted || !containerSize || !imageNaturalSize) return;
      const view = frameBox(box, containerSize, fitted, imageNaturalSize);
      if (view) setViewState(view);
    },
    [fitted, containerSize, imageNaturalSize],
  );

  const deletePoint = useCallback(
    (polygonId: string, pointIndex: number) => {
      const updatedPolygons = polygons
        .map(polygon => {
          if (polygon.id === polygonId) {
            const newPoints = polygon.points.filter((_, index) => index !== pointIndex);
            // Don't allow polygons with less than 3 points
            if (newPoints.length < 3) {
              return null; // This will be filtered out
            }
            return { ...polygon, points: newPoints };
          }
          return polygon;
        })
        .filter(Boolean) as Polygon[];

      setPolygons(updatedPolygons);
      onPolygonChange?.(updatedPolygons);
      setShowDeleteButton(null);
      // The keyboard's corner follows its own point: gone with it, or one down past the cut.
      setSelectedPointForKeyboard(prev => {
        if (prev?.polygonId !== polygonId) return prev;
        if (prev.pointIndex === pointIndex || updatedPolygons.every(p => p.id !== polygonId))
          return null;
        return prev.pointIndex < pointIndex ? prev : { polygonId, pointIndex: prev.pointIndex - 1 };
      });
      setHoveredPoint(null);
    },
    [polygons, onPolygonChange],
  );

  const undoLastPoint = useCallback(() => {
    if (!currentPolygon || currentPolygon.points.length === 0) return;

    if (currentPolygon.points.length === 1) {
      // If only one point, cancel the polygon
      setCurrentPolygon(null);
    } else {
      // Remove the last point
      setCurrentPolygon(prev =>
        prev
          ? {
              ...prev,
              points: prev.points.slice(0, -1),
            }
          : null,
      );
    }
  }, [currentPolygon]);

  const cancelRegion = useCallback(() => setCurrentPolygon(null), []);

  // The host's own controls — the header's Undo bar and the dock's Recentre (FR5, FR11).
  useImperativeHandle(ref, () => ({ undoLastPoint, cancelRegion, recentre: resetView, frameTo }), [
    undoLastPoint,
    cancelRegion,
    resetView,
    frameTo,
  ]);

  // The open region's point count, so the header can show the Undo bar (FR5). Read through refs:
  // a host passing a fresh callback each render must not be called again for the same count.
  const openPoints = currentPolygon?.points.length ?? 0;
  const onDrawingChangeRef = useRef(onDrawingChange);
  onDrawingChangeRef.current = onDrawingChange;
  useEffect(() => {
    onDrawingChangeRef.current?.(openPoints);
  }, [openPoints]);

  // The zoom, in percent of the fitted picture, for the dock's readout (FR11).
  const zoomPercent = Math.round(viewState.scale * 100);
  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;
  useEffect(() => {
    onZoomChangeRef.current?.(zoomPercent);
  }, [zoomPercent]);

  useEffect(() => {
    /*
     * ⚠ Scoped to the CAMERA STAGE the plotter sits in (or, outside one, the plotter itself), never
     * the window (`observers-page-camera-stage`, FR18): these keys belong to the picture, and a
     * window listener answered them anywhere on the page — Delete in another field deleted a
     * region. The plotter answers first and stops a key it consumed; the stage answers the rest.
     */
    const outer = outerRef.current;
    const root: HTMLElement | null =
      outer?.closest<HTMLElement>(`[${OBSERVER_STAGE_ATTRIBUTE}]`) ?? outer;
    if (!root) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Shift shows the crosshair over the picture (R3.1); it decides nothing (R3.6).
      if (e.key === 'Shift') setShiftHeld(true);
      const target = e.target as HTMLElement | null;
      // A field keeps its own keys (Ctrl+Z in a text box is the box's undo), and a control laid
      // over the picture (the tracking card, a toast's button) owns its own.
      const typing =
        !!target &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      /*
       * Tab with the pointer over the picture (not over a card on it), focus not in a text field, no
       * region open and nothing armed: held, for Tab+click (R5.2). It does not move focus — the
       * keydown and its auto-repeats are prevented. Asked before the control check below: where the
       * pointer is decides, not where focus is. Everywhere else Tab moves focus as ever.
       */
      if (
        e.key === 'Tab' &&
        !typing &&
        pointerOverPictureRef.current &&
        !currentPolygon &&
        !drawArmed
      ) {
        e.preventDefault();
        if (!tabHeldRef.current) setTabHeld(true);
        return;
      }
      // A Tab that moves focus is not held, so a keyup that may land outside the stage cannot strand
      // it held.
      if (e.key === 'Tab' && tabHeldRef.current) setTabHeld(false);
      if (typing || target?.closest?.('[data-control]')) return;

      if (e.key === 'Escape') {
        if (currentPolygon) {
          // Consumed: the region in progress was the thing to cancel, so the stage does not also
          // act on this Escape (Escape is layered, FR18).
          e.stopPropagation();
          setCurrentPolygon(null);
        }
        return;
      }
      const undoKey =
        ((e.ctrlKey || e.metaKey) &&
          !e.altKey &&
          (e.key === 'z' || e.key === 'Z') &&
          !e.shiftKey) ||
        (e.key === 'Backspace' && !e.ctrlKey && !e.metaKey && !e.altKey);
      if (undoKey) {
        // Consumed only while a region is in progress: with none, Ctrl+Z is the host's undo.
        if (currentPolygon) {
          e.preventDefault();
          e.stopPropagation();
          undoLastPoint();
        }
        return;
      }
      if (e.key === 'Delete') {
        // Only a region this plotter holds: a controlled selection the host has not yet reseeded
        // the plotter for is not one to delete from here.
        if (selectedPolygon && polygons.some(p => p.id === selectedPolygon)) {
          e.stopPropagation();
          if (onDeleteKey) onDeleteKey(selectedPolygon);
          else deletePolygon(selectedPolygon);
        }
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        e.stopPropagation();
        zoomIn();
        return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        e.stopPropagation();
        zoomOut();
        return;
      }
      // Arrows pan the picture while it has focus and no corner is selected; with a corner
      // selected they keep nudging it (the container's own handler, which stops them first).
      const inCanvas = !!target && !!containerRef.current?.contains(target);
      if (inCanvas && !selectedPointForKeyboard && e.key.startsWith('Arrow')) {
        e.preventDefault();
        e.stopPropagation();
        const dx = e.key === 'ArrowLeft' ? 1 : e.key === 'ArrowRight' ? -1 : 0;
        const dy = e.key === 'ArrowUp' ? 1 : e.key === 'ArrowDown' ? -1 : 0;
        setViewState(prev => ({
          ...prev,
          offsetX: prev.offsetX + dx * KEY_PAN_PX,
          offsetY: prev.offsetY + dy * KEY_PAN_PX,
        }));
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(false);
      if (e.key === 'Tab') setTabHeld(false);
    };
    // A keyup never seen (focus left the window) must not leave the crosshair up, nor Tab held.
    const handleBlur = () => {
      setShiftHeld(false);
      setTabHeld(false);
    };

    root.addEventListener('keydown', handleKeyDown);
    root.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      root.removeEventListener('keydown', handleKeyDown);
      root.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [
    currentPolygon,
    selectedPolygon,
    polygons,
    deletePolygon,
    onDeleteKey,
    undoLastPoint,
    drawArmed,
    setTabHeld,
    zoomIn,
    zoomOut,
    selectedPointForKeyboard,
  ]);

  /*
   * The region being edited: the one picked on the canvas, else the observer the host selected.
   * With neither (nothing picked yet) every own region counts.
   */
  const activeEditId = selectedPolygon ?? selectedRegionId ?? null;
  /*
   * The crosshair: a click here places a point (R3.1, R3.3) — a region is open, or Shift is held or
   * Add Observer armed where drawing is allowed. Not while Tab is held: a press then steps the stack
   * (Shift+Tab backwards), as the press handler decides Tab first. A cursor cue only: the armed state
   * is also the host's pressed button and announcement.
   */
  const drawCursor = currentPolygon !== null || (!tabHeld && pressDraws(shiftHeld));
  const regionCursor = drawCursor ? 'crosshair' : 'pointer';
  /** The open entity's own region a Tab+click would select, lit with the hover look (R5.6). */
  const [tabLitOwnId, setTabLitOwnId] = useState<string | null>(null);
  const ownKindOf = (id: string, isCurrent: boolean): RegionKind =>
    !isCurrent && tabHeld && tabLitOwnId === id
      ? 'hover'
      : ownRegionKind({
          isCurrent,
          waiting: regionsWaiting,
          active: activeEditId === null || id === activeEditId,
        });

  const renderPolygon = (polygon: Polygon, isCurrent = false) => {
    if (polygon.points.length === 0) return null;

    // How this region is drawn — the token table (`regionStyle.ts`, #1865 FR8).
    const kind = ownKindOf(polygon.id, isCurrent);
    const look = REGION_STYLE[kind];

    // Always render points
    const points = polygon.points.map((point, index) => {
      const screenPoint = getScreenCoordinates(point.x, point.y);
      const isHovered =
        hoveredPoint?.polygonId === polygon.id && hoveredPoint?.pointIndex === index;
      const isSelectedForKeyboard =
        selectedPointForKeyboard?.polygonId === polygon.id &&
        selectedPointForKeyboard?.pointIndex === index;

      return (
        <circle
          key={index}
          cx={screenPoint.x}
          cy={screenPoint.y}
          r={isHovered ? look.handleR + 2 : look.handleR}
          fill={isSelectedForKeyboard ? KEYBOARD_HANDLE_FILL : look.handleFill}
          stroke={look.handleStroke}
          strokeWidth={look.handleWidth}
          // Every corner takes a right-click (#1913), while a region is open too, when the overlay
          // turns pointer events off; the canvas's own handlers work from coordinates, so a left
          // press is unchanged.
          style={{ cursor: regionCursor, pointerEvents: 'auto' }}
          onClick={e => {
            e.stopPropagation();
            // Only a press that selects: a press that drew or added a point is not a corner pick.
            if (!clickSelects()) return;
            // Select this point for keyboard navigation
            setSelectedPointForKeyboard({ polygonId: polygon.id, pointIndex: index });
            // Focus the container to enable keyboard events
            if (containerRef.current) {
              containerRef.current.focus();
            }
          }}
          // Right-click deletes the corner (#1913): on a finished region never below 3 corners —
          // `deletePoint` would drop the whole region, nor under a corner drag (a macOS Ctrl+click
          // presses the left button first); on the open region, any of its points.
          onContextMenu={e => {
            e.preventDefault();
            e.stopPropagation();
            // Touch and pen keep the long-press bubble: their press-and-hold raises this too.
            const { pointerType } = e.nativeEvent as PointerEvent;
            if (pointerType && pointerType !== 'mouse') return;
            if (isCurrent) {
              setCurrentPolygon(prev => {
                if (!prev || prev.points.length <= 1) return null;
                return { ...prev, points: prev.points.filter((_, i) => i !== index) };
              });
            } else if (!isDraggingPoint && polygon.points.length > 3) {
              deletePoint(polygon.id, index);
            }
          }}
        />
      );
    });

    // Render optimized path with event delegation
    let strokePath = null;
    let hitPath = null; // Invisible wider path for better click detection

    if (polygon.points.length >= 2) {
      const pathCommands = [];

      // Start with the first point
      if (polygon.points.length > 0) {
        const firstScreen = getScreenCoordinates(polygon.points[0].x, polygon.points[0].y);
        pathCommands.push(`M ${firstScreen.x} ${firstScreen.y}`);
      }

      // Add lines to subsequent points
      for (let i = 1; i < polygon.points.length; i++) {
        const currentPoint = polygon.points[i];
        const currentScreen = getScreenCoordinates(currentPoint.x, currentPoint.y);
        pathCommands.push(`L ${currentScreen.x} ${currentScreen.y}`);
      }

      // Add closing line for complete polygons
      if (polygon.isComplete && polygon.points.length >= 3) {
        pathCommands.push('Z');
      }

      const pathData = pathCommands.join(' ');
      const isLineHovered = hoveredLine?.polygonId === polygon.id;
      const isSelected = selectedPolygon === polygon.id;

      const strokeColor = look.stroke;
      strokePath = (
        <path
          d={pathData}
          fill="none"
          stroke={strokeColor}
          strokeDasharray={look.dash}
          strokeLinejoin="round"
          strokeWidth={isLineHovered ? look.width + 1 : isSelected ? look.width + 0.5 : look.width}
          style={{
            cursor: regionCursor,
            opacity: isLineHovered ? 0.8 : 1,
          }}
          onClick={e => {
            e.stopPropagation();
            // Only a press that selects: a press that drew or added a point leaves the edge alone.
            if (!clickSelects()) return;
            const clickedSegment = getClickedLineSegment(polygon, e.clientX, e.clientY);
            if (clickedSegment !== null) {
              // Add new point at click location
              const clickPoint = getImageCoordinates(e.clientX, e.clientY);
              if (clickPoint) {
                insertPoint(polygon.id, clickedSegment, clickPoint);
              }
            } else {
              selectPolygon(polygon.id);
            }
          }}
        />
      );

      // Invisible wider hit area for better click detection — a finished region's only.
      if (!isCurrent) {
        hitPath = (
          <path
            d={pathData}
            fill="none"
            stroke="transparent"
            strokeWidth={12} // Much wider for easier clicking
            style={{
              cursor: regionCursor,
              pointerEvents: 'stroke',
            }}
            onClick={e => {
              e.stopPropagation();
              if (!clickSelects()) return;
              const clickedSegment = getClickedLineSegment(polygon, e.clientX, e.clientY);
              if (clickedSegment !== null) {
                const clickPoint = getImageCoordinates(e.clientX, e.clientY);
                if (clickPoint) {
                  insertPoint(polygon.id, clickedSegment, clickPoint);
                }
              } else {
                selectPolygon(polygon.id);
              }
            }}
          />
        );
      }
    }

    // Fill path for completed polygons
    let fillPath = null;
    if (polygon.isComplete && polygon.points.length >= 3) {
      const pathData = polygon.points
        .map((point, index) => {
          const screenPoint = getScreenCoordinates(point.x, point.y);
          return `${index === 0 ? 'M' : 'L'} ${screenPoint.x} ${screenPoint.y}`;
        })
        .join(' ');

      const fillColor = look.fill;
      fillPath = (
        <path
          d={`${pathData} Z`}
          fill={fillColor}
          stroke="none"
          style={{ pointerEvents: 'none' }}
        />
      );
    }

    return (
      <g
        key={polygon.id}
        data-plotter-region=""
        data-region-id={polygon.id}
        data-region-kind={kind}
      >
        {fillPath}
        {hitPath}
        {strokePath}
        {points}
      </g>
    );
  };

  /*
   * ── The region hover tip ──────────────────────────────────────────────────────────────────────
   *
   * Which region the pointer rests on, found by a point-in-polygon test in image coordinates rather
   * than by SVG hit-testing: the backdrop's outlines are `pointer-events: none` (they are not
   * editable, and must not become so to be named), and the plotter's own hit paths exist only for
   * finished regions. The smallest region under the pointer wins, own or backdrop, with a tie to the open
   * entity's own — the same rule a click follows (`observer-overlap-pick`, FR2).
   *
   * `tippedRegions` is the ONE statement of which regions can be named: complete, not hidden (the
   * per-observer toggle for its own regions, the backdrop toggle for the rest) and carrying a tip.
   * The hit test, the tip shown and the text description all read it, so a region cannot be
   * hoverable while missing from the description, or the reverse.
   */
  const tippedRegions = useMemo(() => {
    if (!polygonTips) return { own: [] as Polygon[], frame: [] as Polygon[] };
    return {
      own: polygons.filter(p => p.isComplete && polygonTips[p.id]),
      frame: showFramePolygons
        ? framePolygons.filter(p => p.isComplete && polygonTips[p.id])
        : ([] as Polygon[]),
    };
  }, [polygonTips, polygons, showFramePolygons, framePolygons]);
  // The hit-test's order only: the description keeps the regions' own order.
  const tippedByArea = useMemo(
    () => [bySmallestArea(tippedRegions.own), bySmallestArea(tippedRegions.frame)],
    [tippedRegions],
  );
  /*
   * The card names what a click picks, so it never shows for the open entity's OWN regions:
   * there it says nothing new and covers the very corners being dragged
   * (`observer-edit-hover-card`). Their tips stay — the regions description reads them. Ruled out
   * at the hover, not only at render, so leaving an own region for a backdrop one still waits the
   * first-entry delay. While Tab is held the card names what a Tab+click takes, own or not (R5.6):
   * the operator cannot otherwise tell which of two near-identical regions is next.
   */
  const ownCardHidden = (id: string): boolean =>
    !tabHeld && tippedRegions.own.some(p => p.id === id);

  const [hoveredRegionId, setHoveredRegionId] = useState<string | null>(null);
  /** The region the tip is SHOWING for — `hoveredRegionId` once the first-entry delay has run. */
  const [tipRegionId, setTipRegionId] = useState<string | null>(null);
  const tipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = useId();
  const regionsDescriptionId = useId();

  const regionAt = useCallback(
    (clientX: number, clientY: number): string | null => {
      const point = getImageCoordinates(clientX, clientY);
      if (!point) return null;
      const [own, other] = tippedByArea.map(list =>
        list.find(p => pointInPolygon(point, p.points)),
      );
      if (own && other) return otherIsSmaller(own, other) ? other.id : own.id;
      return (own ?? other)?.id ?? null;
    },
    [tippedByArea, getImageCoordinates],
  );

  /*
   * The BACKDROP region under the pointer, smallest first — what a click picks (#1865, FR9;
   * `observer-overlap-pick`, FR2).
   * Separate from `regionAt`, which also answers for the plotter's own regions and only for regions
   * carrying a tip: picking must work on any region shown.
   */
  const backdropRegionAt = useCallback(
    (clientX: number, clientY: number): string | null => {
      if (!showFramePolygons) return null;
      const point = getImageCoordinates(clientX, clientY);
      if (!point) return null;
      return framesByArea.find(p => p.isComplete && pointInPolygon(point, p.points))?.id ?? null;
    },
    [showFramePolygons, framesByArea, getImageCoordinates],
  );
  backdropRegionAtRef.current = backdropRegionAt;
  /** The backdrop region a click would pick, lit with the dashed frost hover look (#1865, FR9). */
  const [hoveredBackdropId, setHoveredBackdropId] = useState<string | null>(null);
  // Picking switched off ends the hover with it — a stale id kept the backdrop lit (#1865 review).
  useEffect(() => {
    if (!onPickRegion) setHoveredBackdropId(null);
  }, [onPickRegion]);
  const onHoverRegionRef = useRef(onHoverRegion);
  onHoverRegionRef.current = onHoverRegion;
  useEffect(() => {
    onHoverRegionRef.current?.(hoveredBackdropId);
  }, [hoveredBackdropId]);

  /*
   * Another entity's region: a faint frost outline, or — where a click would pick it — the dashed
   * frost hover outline of exactly the region the pointer is over (#1865 FR8, FR9). No handles. Drawn in the
   * picture's own pixels (the group carries the view), with a stroke that does not scale with it.
   * Largest first, so the smallest is drawn last and sits in front (`observer-overlap-pick`, FR3).
   */
  const backdropShapes = useMemo(
    () =>
      [...framesByArea].reverse().flatMap(polygon => {
        if (polygon.points.length < 2) return [];
        const hovered = !!onPickRegion && hoveredBackdropId === polygon.id;
        const look = REGION_STYLE[hovered ? 'hover' : 'ghost'];
        const pathData = polygon.points
          .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
          .join(' ');
        return [
          <g
            key={`frame-${polygon.id}`}
            data-plotter-frame-region=""
            data-region-id={polygon.id}
            data-region-kind={hovered ? 'hover' : 'ghost'}
          >
            <path
              d={polygon.isComplete ? `${pathData} Z` : pathData}
              fill={polygon.isComplete ? look.fill : 'none'}
              stroke={look.stroke}
              strokeWidth={look.width}
              strokeDasharray={look.dash}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{ cursor: hovered ? 'pointer' : 'default', pointerEvents: 'none' }}
            />
          </g>,
        ];
      }),
    [framesByArea, onPickRegion, hoveredBackdropId],
  );

  /*
   * No tip while the operator is placing or moving a point, while a click would draw (R3.1), while
   * the canvas is panning, or over a control floating on the canvas: the tip is information, and
   * must never sit on the point being placed.
   */
  const tipSuppressed =
    !isRealImage ||
    !imageLoaded ||
    drawCursor ||
    isDraggingPoint ||
    draggedPoint !== null ||
    (isDragging && !!pressRef.current?.moved);

  const updateHoverAt = (
    clientX: number,
    clientY: number,
    shiftKey: boolean,
    overControl: boolean,
  ) => {
    /*
     * Tab held: what is lit and named is what a Tab+click takes (R5.6), own or another entity's.
     */
    if (tabHeld && !currentPolygon && !drawArmed) {
      const point = overControl ? null : getImageCoordinates(clientX, clientY);
      const stack = point ? steppableAt(point) : [];
      const target = stepInStack(stack, selectedPolygon, shiftKey);
      setHoveredBackdropId(target && !target.own ? target.id : null);
      setTabLitOwnId(target?.own ? target.id : null);
      if (polygonTips) setHoveredRegionId(target && isTipped(target.id) ? target.id : null);
      return;
    }
    setTabLitOwnId(null);
    // The hover lights only what a press would pick (`observer-overlap-pick`, FR4): never over the
    // open entity's own corner or edge, nor inside an own region smaller than the region behind it.
    const wouldPick = () => {
      const point = getImageCoordinates(clientX, clientY);
      if (!point) return null;
      const own = ownControlAt(clientX, clientY);
      if (own.point || own.line) return null;
      return insideAt(point).otherId;
    };
    // Nothing is lit while a click would draw: this event's Shift, not the last key's (R3.1).
    const drawNow = !!currentPolygon || pressDraws(shiftKey);
    setHoveredBackdropId(onPickRegion && !overControl && !drawNow ? wouldPick() : null);
    if (!polygonTips) return;
    const region = tipSuppressed || drawNow || overControl ? null : regionAt(clientX, clientY);
    setHoveredRegionId(region !== null && ownCardHidden(region) ? null : region);
  };
  const updateHoveredRegion = (e: React.MouseEvent) => {
    const overControl = !!(e.target as Element | null)?.closest?.('[data-control]');
    pointerOverPictureRef.current = !overControl;
    // A card on the picture is off it (R5.2): Tab held there is let go.
    if (overControl && tabHeldRef.current) setTabHeld(false);
    lastPointerRef.current = { x: e.clientX, y: e.clientY, shiftKey: e.shiftKey };
    updateHoverAt(e.clientX, e.clientY, e.shiftKey, overControl);
  };
  /*
   * Tab or Shift pressed or released, or the selection or regions changed (a Tab+click), with the
   * pointer still: relight from where it rests, with Shift as it is now (R3.1, R5.6).
   */
  const updateHoverAtRef = useRef(updateHoverAt);
  updateHoverAtRef.current = updateHoverAt;
  useEffect(() => {
    const at = lastPointerRef.current;
    if (at && pointerOverPictureRef.current) updateHoverAtRef.current(at.x, at.y, shiftHeld, false);
  }, [tabHeld, shiftHeld, selectedPolygon, polygons, framePolygons]);

  // The first entry waits the card's delay; moving from one region straight to another does not,
  // because the operator is already reading tips. Leaving every region hides it at once.
  useEffect(() => {
    if (tipTimerRef.current) {
      clearTimeout(tipTimerRef.current);
      tipTimerRef.current = null;
    }
    if (hoveredRegionId === null) {
      setTipRegionId(null);
      return;
    }
    if (tipRegionId !== null) {
      setTipRegionId(hoveredRegionId);
      return;
    }
    tipTimerRef.current = setTimeout(() => {
      tipTimerRef.current = null;
      setTipRegionId(hoveredRegionId);
    }, REGION_TIP_DELAY_MS);
    // `tipRegionId` is read, not reacted to: only a change of HOVER may start or skip the delay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredRegionId]);
  useEffect(
    () => () => {
      if (tipTimerRef.current) clearTimeout(tipTimerRef.current);
    },
    [],
  );
  // Starting to draw, or holding Shift, drops a tip that is already up.
  useEffect(() => {
    if (tipSuppressed) setHoveredRegionId(null);
  }, [tipSuppressed]);

  /*
   * The tip shown: the hovered region's. A region since hidden, removed or untipped shows nothing.
   * The selected region is never named without a pointer: it is always the open entity's own, which
   * the card does not name, and the regions description says it (`observer-unified-draw-edit`).
   */
  const isTipped = (id: string): boolean =>
    tippedRegions.own.some(p => p.id === id) || tippedRegions.frame.some(p => p.id === id);
  const shownTip =
    tipRegionId !== null && polygonTips && isTipped(tipRegionId) && !ownCardHidden(tipRegionId)
      ? polygonTips[tipRegionId]
      : null;
  // A hovered region that can no longer show a tip — hidden, removed, or replaced by a pick that
  // reseeds this plotter — is dropped, so the next region entered waits the first-entry delay
  // instead of inheriting a tip nobody was reading.
  const hoverStale = hoveredRegionId !== null && !isTipped(hoveredRegionId);
  useEffect(() => {
    if (hoverStale) setHoveredRegionId(null);
  }, [hoverStale]);

  /*
   * Where it sits: above the region where there is room, below where there is room there, and —
   * for a region that fills the picture's height, where neither side has room — over the region's
   * middle, held inside the canvas. The canvas clips (`overflow: hidden`), so a tip placed outside
   * it would be mounted and invisible.
   */
  const tipPlacement = (() => {
    if (!shownTip || tipRegionId === null) return null;
    const region =
      tippedRegions.own.find(p => p.id === tipRegionId) ??
      tippedRegions.frame.find(p => p.id === tipRegionId);
    if (!region || region.points.length === 0) return null;
    const screen = region.points.map(pt => getScreenCoordinates(pt.x, pt.y));
    const xs = screen.map(pt => pt.x);
    const ys = screen.map(pt => pt.y);
    const top = Math.min(...ys);
    const bottom = Math.max(...ys);
    const box = containerRef.current?.getBoundingClientRect();
    const width = containerRef.current?.clientWidth || box?.width || 0;
    const height = containerRef.current?.clientHeight || box?.height || 0;
    const half = width > 0 ? Math.min(REGION_TIP_HALF_WIDTH_PX, width / 2) : 0;
    const centre = (Math.min(...xs) + Math.max(...xs)) / 2;
    const x = width > 0 ? Math.min(Math.max(centre, half), width - half) : centre;
    if (top >= REGION_TIP_HEIGHT_PX) return { x, y: top };
    if (height === 0 || height - bottom >= REGION_TIP_HEIGHT_PX) return { x, y: bottom };
    const middle = (top + bottom) / 2;
    return { x, y: Math.min(Math.max(middle, REGION_TIP_HEIGHT_PX), height) };
  })();

  /*
   * Every visible tipped region, as text: the substitute for the hover tip that the keyboard and a
   * screen reader can reach. Own regions first, then the backdrop — the order they paint in.
   */
  const regionsDescription = useMemo(() => {
    const lines = [...tippedRegions.own, ...tippedRegions.frame].map(p => {
      const tip = polygonTips![p.id];
      return `${tip.title}, ${tip.detail}`;
    });
    return lines.length > 0 ? `Observers on this picture: ${lines.join('; ')}.` : '';
  }, [tippedRegions, polygonTips]);
  // The suggestions' text alternative (R7): the outlines are drawn, so the canvas says what they are.
  const shownSuggestions = suggestionsVisible ? suggestions : NO_SUGGESTIONS;
  const suggestionsDescriptionText =
    shownSuggestions.length > 0
      ? `Suggested: ${shownSuggestions.map(s => s.label).join('; ')}.`
      : '';
  const suggestionsDescriptionId = useId();
  // The machine outlines' text alternative: they have no DOM a screen reader can reach, so the
  // canvas names the machines whose outline is drawn, each once, in list order. Drawn only over a
  // real picture that has loaded, so named only then: never outlines the canvas is not showing.
  const shownMachineOutlines = (
    suggestionsVisible && isRealImage && imageLoaded ? machineOutlines : NO_MACHINE_OUTLINES
  ).filter(o => o.points.length >= 3);
  // One name per machine (not per name: two machines can share one), in list order.
  const outlineNames = Array.from(
    new Map(shownMachineOutlines.map(o => [o.machine, o.name] as const)).values(),
  );
  const machineOutlinesDescriptionText =
    outlineNames.length > 0 ? `Machine outlines: ${outlineNames.join(', ')}.` : '';
  const machineOutlinesDescriptionId = useId();
  const canvasDescribedBy =
    [
      regionsDescription ? regionsDescriptionId : null,
      suggestionsDescriptionText ? suggestionsDescriptionId : null,
      machineOutlinesDescriptionText ? machineOutlinesDescriptionId : null,
      describedBy ?? null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  /** The picture's pixels → the overlay's, by the same rule every point is placed by. */
  const pictureTransform = (() => {
    const img = imageRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return undefined;
    const o = getScreenCoordinates(0, 0);
    const c = getScreenCoordinates(img.naturalWidth, img.naturalHeight);
    const sx = (c.x - o.x) / img.naturalWidth;
    const sy = (c.y - o.y) / img.naturalHeight;
    return `matrix(${sx} 0 0 ${sy} ${o.x} ${o.y})`;
  })();

  return (
    <OuterContainer ref={outerRef} className={className}>
      <Container
        ref={setContainerNode}
        {...{ [OBSERVER_PLOTTER_ATTRIBUTE]: '' }}
        onMouseDown={handleMouseDown}
        onMouseMove={e => {
          // The pointer's own Shift, so the crosshair follows a Shift pressed while focus was
          // elsewhere (R3.6).
          setShiftHeld(e.shiftKey);
          handleMouseMove(e);
          updateHoveredRegion(e);
        }}
        style={drawCursor ? { cursor: 'crosshair' } : undefined}
        onMouseLeave={() => {
          setHoveredRegionId(null);
          setHoveredBackdropId(null);
          // Off the picture, Tab moves focus again (R5.2).
          pointerOverPictureRef.current = false;
          setTabHeld(false);
          setTabLitOwnId(null);
        }}
        onMouseUp={handleMouseUp}
        onBlur={endNudge}
        // Always a tab stop, with no region yet too: the arrows pan the picture and
        // the zoom keys act only while it has focus (FR18), and the regions description it carries
        // is the keyboard route to what the hover tip says (R3.7).
        tabIndex={0}
        role="application"
        aria-describedby={canvasDescribedBy}
        aria-label={`${!imgSrc && emptyMessage ? `${emptyMessage}. ` : ''}${
          canvasLabel ??
          'Polygon editor - click points to select them for keyboard navigation, use arrow keys to move selected points'
        }`}
        aria-busy={heatmapLoading || undefined}
      >
        {suggestionsDescriptionText && (
          <span id={suggestionsDescriptionId} style={visuallyHidden}>
            {suggestionsDescriptionText}
          </span>
        )}
        {machineOutlinesDescriptionText && (
          <span id={machineOutlinesDescriptionId} style={visuallyHidden}>
            {machineOutlinesDescriptionText}
          </span>
        )}
        {regionsDescription && (
          <span id={regionsDescriptionId} style={visuallyHidden}>
            {regionsDescription}
          </span>
        )}
        {shownTip && tipPlacement && (
          /*
           * The hover is what a click picks (v2 design, state 06): the design-system HoverCard, with
           * the body part as a lavender tag and *Click to edit* (`observers-page-camera-stage`, FR20).
           */
          <HoverCard
            open
            id={tipId}
            anchor={{ x: tipPlacement.x, y: tipPlacement.y }}
            title={shownTip.title}
            code={shownTip.code}
            status={{ label: `Tracks ${shownTip.detail}`, tone: 'lavender' }}
            hint={{ label: 'Click to edit' }}
          />
        )}

        <ImageContainer>
          {imgSrc && (
            <StyledImage
              ref={imageRef}
              src={imgSrc}
              alt="Polygon plotting area"
              onLoad={handleImageLoad}
              // Suppress the browser's native image drag-and-drop: without this,
              // grabbing a polygon point (which overlays the image) starts a
              // native image drag that swallows the mousemove events the point
              // drag relies on, so points can't be repositioned.
              draggable={false}
              onDragStart={e => e.preventDefault()}
              style={{
                // Fitted to the canvas at the picture's own ratio, centred by the container; the
                // zoom/pan transform sits on top. Until the natural size is known the CSS caps hold.
                ...(fitted ? { width: `${fitted.w}px`, height: `${fitted.h}px` } : {}),
                transform: `scale(${viewState.scale}) translate(${viewState.offsetX / viewState.scale}px, ${viewState.offsetY / viewState.scale}px)`,
                transformOrigin: '0 0',
                // Its own compositor layer, so a zoom scales the decoded picture rather than
                // re-rasterising a camera frame on every wheel notch (FR28, measured).
                willChange: 'transform',
              }}
            />
          )}

          <SVGOverlay $interactive={!currentPolygon}>
            {/* The heat history, in the same natural-image coordinate space as the polygons. Drawn
                FIRST, under every region, so the region being drawn, its handles and the other
                observers' dotted outlines stay visible over a busy camera. */}
            {showHeatmap && isRealImage && imageLoaded && heatImage && (
              /*
               * In the PICTURE's coordinates, inside one group that carries the view (FR28): a zoom
               * or pan changes only the group's transform, never the image.
               */
              <g data-heat-layer="" transform={pictureTransform}>
                <image
                  data-heat-image=""
                  href={heatImage.url}
                  x={0}
                  y={0}
                  width={heatImage.width * HEAT_GRID_CELL_PX}
                  height={heatImage.height * HEAT_GRID_CELL_PX}
                  preserveAspectRatio="none"
                  style={{ imageRendering: 'auto' }}
                  pointerEvents="none"
                  role="img"
                  aria-label={heatImage.label}
                  onLoad={() => markHeatGrid('painted')}
                />
              </g>
            )}
            {/* Heat outlines, over the heat and under every region. Taken by a click over no region
                (the press handler), so the layer itself takes no pointer events. */}
            {shownHeatOutlines.length > 0 && (
              <g
                data-heat-outlines=""
                role="group"
                aria-label="Heat outlines"
                pointerEvents="none"
                style={{ pointerEvents: 'none' }}
                transform={pictureTransform}
              >
                {shownHeatOutlines.map((outline, index) => {
                  const style =
                    index === outlineHighlight
                      ? REGION_STYLE.heatOutlineHighlight
                      : REGION_STYLE.heatOutline;
                  return (
                    <path
                      key={`heat-outline-${index}`}
                      data-heat-outline={index}
                      data-highlighted={index === outlineHighlight ? '' : undefined}
                      d={`${outline.points
                        .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`)
                        .join(' ')} Z`}
                      fill={style.fill}
                      stroke={style.stroke}
                      strokeWidth={style.width}
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
              </g>
            )}
            {isRealImage && imageLoaded && showFramePolygons && framePolygons.length > 0 && (
              // The backdrop, like the heat, in the picture's pixels under one view transform: a
              // zoom or pan moves the group, never rebuilds the outlines (FR28).
              <g data-backdrop-layer="" transform={pictureTransform}>
                {backdropShapes}
              </g>
            )}
            {/* Machine outlines (`observer-matching-beyond-geometry-machine-outline`): where the engine
                thinks each machine is. Read-only and pointer-transparent, after the backdrop and
                BEFORE the suggestions, so a suggestion draws over an outline and the operator's
                regions over both. No label: the canvas's description names them. */}
            {shownMachineOutlines.length > 0 && (
              <g
                data-machine-outline-layer=""
                pointerEvents="none"
                style={{ pointerEvents: 'none' }}
                transform={pictureTransform}
              >
                {shownMachineOutlines.map(outline => (
                  <path
                    key={`machine-outline-${outline.id}`}
                    data-machine-outline={outline.id}
                    d={`${outline.points
                      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
                      .join(' ')} Z`}
                    fill={REGION_STYLE.machineOutline.fill}
                    stroke={REGION_STYLE.machineOutline.stroke}
                    strokeWidth={REGION_STYLE.machineOutline.width}
                    strokeDasharray={REGION_STYLE.machineOutline.dash}
                    strokeLinecap={REGION_STYLE.machineOutline.lineCap}
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    opacity={outline.faint ? FAINT_SUGGESTION_OPACITY : undefined}
                    data-faint={outline.faint ? '' : undefined}
                  />
                ))}
              </g>
            )}
            {/* Suggestions (`observer-autogen-review-ui-preview`, R4): read-only, after the backdrop
                and before the operator's own regions, so a region being drawn stays on top. The
                whole layer ignores the pointer — a click falls through to drawing as on empty
                picture. Outlines in the picture's pixels under the view; labels in screen pixels so
                they keep their size at any zoom. */}
            {isRealImage && imageLoaded && shownSuggestions.length > 0 && (
              <g data-suggestion-layer="" pointerEvents="none" style={{ pointerEvents: 'none' }}>
                <g transform={pictureTransform}>
                  {shownSuggestions
                    .filter(sug => sug.points.length >= 3)
                    .map(sug => (
                      <path
                        key={`suggestion-${sug.id}`}
                        data-suggestion={sug.id}
                        d={`${sug.points
                          .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
                          .join(' ')} Z`}
                        fill={REGION_STYLE.suggestion.fill}
                        stroke={REGION_STYLE.suggestion.stroke}
                        strokeWidth={REGION_STYLE.suggestion.width}
                        strokeDasharray={REGION_STYLE.suggestion.dash}
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                        opacity={sug.faint ? FAINT_SUGGESTION_OPACITY : undefined}
                        data-faint={sug.faint ? '' : undefined}
                      />
                    ))}
                </g>
                {shownSuggestions
                  // Labels only for the suggestions under review, never the faint background (R3.3).
                  .filter(sug => sug.points.length >= 3 && !sug.faint)
                  .map(sug => {
                    // Kept inside the canvas: the engine clips outlines to the frame, so one can
                    // touch the top edge, and a long label can run past the right (Copilot, #2144).
                    const at = placeSuggestionLabel(
                      sug.points.map(pt => getScreenCoordinates(pt.x, pt.y)),
                      sug.label,
                      {
                        w: containerRef.current?.clientWidth || containerSize?.w || 0,
                        h: containerRef.current?.clientHeight || containerSize?.h || 0,
                      },
                    );
                    return (
                      <text
                        key={`suggestion-label-${sug.id}`}
                        data-suggestion-label={sug.id}
                        x={at.x}
                        y={at.y}
                        fill={surface[0]}
                        stroke={semantic.text.primary}
                        strokeWidth={3}
                        paintOrder="stroke"
                        fontSize={12}
                        fontWeight={600}
                      >
                        {sug.label}
                      </text>
                    );
                  })}
              </g>
            )}
            {/* The rest of the picture dims around the region being edited — the active one only,
                never every own region (#1865 FR8 and its review). Not around a region waiting for
                its entity: there is no entity to edit yet. */}
            {isRealImage && imageLoaded && !regionsWaiting && polygons.length > 0 && (
              <path
                data-edit-dim=""
                fillRule="evenodd"
                fill={EDIT_DIM}
                pointerEvents="none"
                d={[
                  `M -10000 -10000 H 10000 V 10000 H -10000 Z`,
                  ...polygons
                    .filter(
                      p =>
                        p.isComplete &&
                        p.points.length >= 3 &&
                        (activeEditId === null || p.id === activeEditId),
                    )
                    .map(
                      p =>
                        p.points
                          .map((pt, i) => {
                            const sp = getScreenCoordinates(pt.x, pt.y);
                            return `${i === 0 ? 'M' : 'L'} ${sp.x} ${sp.y}`;
                          })
                          .join(' ') + ' Z',
                    ),
                ].join(' ')}
              />
            )}
            {/* Largest first, so the smallest sits in front (`observer-overlap-pick`, FR3). */}
            {isRealImage &&
              imageLoaded &&
              [...ownByArea].reverse().map(polygon => renderPolygon(polygon))}
            {isRealImage && imageLoaded && currentPolygon && renderPolygon(currentPolygon, true)}

            {/* Delete button for long press */}
            {showDeleteButton && (
              <circle
                cx={showDeleteButton.x + DELETE_BUTTON_OFFSET_X}
                cy={showDeleteButton.y + DELETE_BUTTON_OFFSET_Y}
                r={DELETE_BUTTON_RADIUS}
                fill={semanticColor.errorStrong}
                stroke={surface[0]}
                strokeWidth={2}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                data-delete-button
                onClick={e => {
                  e.stopPropagation();
                  deletePoint(showDeleteButton.polygonId, showDeleteButton.pointIndex);
                }}
              />
            )}
          </SVGOverlay>

          {/* Delete icon overlay for long press */}
          {showDeleteButton && (
            <DeleteButtonOverlay
              style={{
                left: showDeleteButton.x + DELETE_BUTTON_OFFSET_X - DELETE_ICON_OFFSET,
                top: showDeleteButton.y + DELETE_BUTTON_OFFSET_Y - DELETE_ICON_OFFSET,
              }}
              onClick={e => {
                e.stopPropagation();
                deletePoint(showDeleteButton.polygonId, showDeleteButton.pointIndex);
              }}
              data-delete-button
            >
              <Icon name="xmark" size="xs" />
            </DeleteButtonOverlay>
          )}

          {(!isRealImage || !imageLoaded) && (
            <AwaitingFeedOverlay data-control>
              <AwaitingFeedMessage>
                {emptyMessage ?? 'Awaiting camera feed to draw polygons'}
              </AwaitingFeedMessage>
            </AwaitingFeedOverlay>
          )}
        </ImageContainer>

        {/* A plotter that owns its selection names it, with its own Delete. A host that owns it
            shows and deletes it itself (the entity card's *Delete Observer*, FR15). */}
        {!selectionControlled && selectedPolygon && (
          <PolygonInfo data-control>
            <div>Selected: {selectedPolygon}</div>
            <Button
              type="button"
              variant="destructive"
              size="xs"
              data-control
              onClick={() => deletePolygon(selectedPolygon)}
            >
              Delete Polygon
            </Button>
          </PolygonInfo>
        )}
      </Container>
    </OuterContainer>
  );
});

/** A press must travel this far (px) before it is a pan rather than a click (#1865, FR12). */
const DRAG_THRESHOLD_PX = 4;
/** Zoom per wheel notch, and the zoom range the wheel and keys share. */
const WHEEL_ZOOM_STEP = 1.1;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5;
/** How far one arrow press pans the picture (px). */
const KEY_PAN_PX = 40;

/**
 * The largest box of the picture's own aspect ratio that fits inside the canvas — the letterbox
 * arithmetic, exported so it can be pinned without a layout engine. Upscales as readily as it
 * downscales: a stored frame smaller than the workspace is still the thing being drawn on, and a
 * 180px thumbnail at natural size in a 1500px canvas is what this replaced
 * (`camera-observer-setup-workspace`, FR2).
 */
/**
 * The view (zoom and pan) that frames `box` — in the picture's natural pixels — in the canvas with a
 * 15 % margin, centred (R3.1). The picture is drawn at `fitted` size, centred in `container`, and the
 * view's transform scales about the picture's own top-left corner.
 */
export const frameBox = (
  box: { x: number; y: number; w: number; h: number },
  container: { w: number; h: number },
  fitted: { w: number; h: number },
  natural: { w: number; h: number },
): { scale: number; offsetX: number; offsetY: number } | null => {
  if (natural.w <= 0 || natural.h <= 0 || fitted.w <= 0 || fitted.h <= 0) return null;
  const k = fitted.w / natural.w; // natural → fitted pixels
  const bw = Math.max(box.w, 1) * k * 1.3;
  const bh = Math.max(box.h, 1) * k * 1.3;
  const scale = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, Math.min(container.w / bw, container.h / bh)),
  );
  const left = (container.w - fitted.w) / 2;
  const top = (container.h - fitted.h) / 2;
  const cx = (box.x + box.w / 2) * k;
  const cy = (box.y + box.h / 2) * k;
  return {
    scale,
    offsetX: container.w / 2 - left - scale * cx,
    offsetY: container.h / 2 - top - scale * cy,
  };
};

export const fitInto = (
  container: { w: number; h: number },
  natural: { w: number; h: number },
): { w: number; h: number } | null => {
  if (container.w <= 0 || container.h <= 0 || natural.w <= 0 || natural.h <= 0) return null;
  const scale = Math.min(container.w / natural.w, container.h / natural.h);
  return { w: natural.w * scale, h: natural.h * scale };
};

const OuterContainer = styled.div`
  margin: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
  border: none;
  border-radius: 0;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  width: 100%;
  /* Fills the plotter — the host sizes it, and this was a 600px cap that let the picture use a
     fixed strip of a viewport-sized workspace. */
  min-height: 0;
  flex: 1;
  position: relative;
  background-color: ${semantic.surface.panelTint};
  overflow: hidden;
  user-select: none;
  box-sizing: border-box;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* The letterbox: the fitted picture sits centred and the noise shows around it. Every
     screen↔image mapping reads the image's own bounding box, so its offset is accounted for. */
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url('/tv-noise.png');
  background-repeat: repeat;
`;

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  object-fit: contain;
`;

const SVGOverlay = styled.svg<{ $interactive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;

  /* The regions take the pointer, except while a region is open: then every click adds a point. */
  path,
  circle {
    pointer-events: ${props => (props.$interactive ? 'auto' : 'none')};
  }
`;

const PolygonInfo = styled.div`
  position: absolute;
  bottom: 60px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AwaitingFeedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  pointer-events: none;
`;

const AwaitingFeedMessage = styled.div`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.2);
`;

const DeleteButtonOverlay = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  color: ${surface[0]};
  cursor: pointer;
  pointer-events: auto;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
  }
`;

export default PolygonPlotter2;
